export interface Translations {
  common: {
    loading: string;
    save: string;
    cancel: string;
    delete: string;
    edit: string;
    back: string;
    next: string;
    confirm: string;
    send: string;
    sending: string;
    search: string;
    noData: string;
    error: string;
    success: string;
    close: string;
    yes: string;
    no: string;
    or: string;
    and: string;
    all: string;
    none: string;
    previous: string;
    nextPage: string;
    page: string;
    of: string;
    justNow: string;
    minutesAgo: string;
    hoursAgo: string;
    daysAgo: string;
  };
  landing: {
    hero: {
      tagline: string;
      description: string;
      cta: string;
      login: string;
      register: string;
      registerNote: string;
      betaBadge: string;
      openApp: string;
    };
    features: {
      title: string;
      subtitle: string;
      goals: { title: string; description: string; badge: string };
      ai: { title: string; description: string };
      todoist: { title: string; description: string };
      calendar: { title: string; description: string };
      archive: { title: string; description: string };
      email: { title: string; description: string };
      absences: { title: string; description: string };
    };
    howItWorks: {
      title: string;
      subtitle: string;
      steps: { title: string; description: string }[];
    };
    footer: { copyright: string };
    cover: {
      edgeLeft: string;
      edgeRight: string;
      nav: {
        product: string;
        method: string;
        pricing: string;
        manifesto: string;
        changelog: string;
        faq: string;
        blog: string;
        signIn: string;
        startTrial: string;
        guides: string;
        productMenu: { label: string; desc: string; href: string }[];
        guidesMenu: { label: string; desc: string; href: string }[];
      };
      hero: {
        kicker: string;
        headline: { line1A: string; line1Em: string; line1B: string; line2A: string; line2Em: string; line2B: string };
        purposeLine: string;
        description: string;
        descriptionShort: string;
        ctaPrimary: string;
        ctaSecondary: string;
        trust: string[];
        calendarNote: string;
        fig: string;
        figLabel: string;
        figSub: string;
        nodes: { code: string; label: string; optional?: boolean; optionalLabel?: string }[];
        goalsLabel: string;
        goalsSub: string;
      };
      chapters: { no: string; title: string; body: string }[];
      showcase: {
        kicker: string;
        h2: string;
        learnMore: string;
        phases: { no: string; title: string; body: string; cta?: string }[];
        screens: {
          plan: {
            date: string;
            title: string;
            tasks: { title: string; time: string }[];
          };
          flow: {
            date: string;
            title: string;
            tasks: { title: string; time: string }[];
            focus: string;
          };
          journal: {
            date: string;
            title: string;
            highlight: string;
            body: string;
            reflection: string;
          };
          tomorrow: {
            date: string;
            title: string;
            intentions: string[];
            note: string;
          };
        };
      };
      journalSection: {
        kicker: string;
        h2a: string;
        h2em: string;
        description: string;
        growth: string;
        annotationTag: string;
        annotationLabel: string;
        cardDate: string;
        cardEntry: string;
        cardBody: string;
        learnMore: string;
      };
      assistant: {
        kicker: string;
        h2a: string;
        h2em: string;
        learnMore: string;
        description: string;
        ctaIntro: string;
        commands: string[];
        demoYou: string;
        demoThinking: string;
        demoIdle: string;
        demoReplayHint: string;
        demoScrollHint: string;
        demoUnderstood: string;
        demoConfirmLabel: string;
        demoChipVerb: string;
        demoChipTip: string;
        demoTaskTitle: string;
        demoTaskWhen: string;
        demoResponses: string[];
        demoLabel: string;
        scrollCue: string;
        closerA: string;
        closerEm: string;
        steps: {
          node: string;
          meta: string;
          ctx: string;
          slash: string;
          cmd: string;
          tag: string;
          ai: string;
        }[];
      };
      languageSection: {
        kicker: string;
        h2a: string;
        h2em: string;
        description: string;
        settingsNote: string;
        mockBreadcrumb: string;
        mockLabel: string;
        mockHint: string;
      };
      aiAssistantPage: {
        commands: {
          kicker: string;
          h2a: string;
          h2em: string;
          description: string;
          naturalLabel: string;
          naturalInput: string;
          slashLabel: string;
          slashInput: string;
          understood: string;
          chipVerb: string;
          chipDetail: string;
          confirmHint: string;
          actionsLead: string;
          actions: string[];
          actionsTail: string;
        };
        memory: {
          kicker: string;
          h2a: string;
          h2em: string;
          description: string;
          pills: string[];
        };
        strategy: {
          kicker: string;
          h2a: string;
          h2em: string;
          description: string;
          dialogue: { user: string; ai: string }[];
        };
      };
      dailyRhythmPage: {
        anatomy: {
          kicker: string;
          h2a: string;
          h2em: string;
          description: string;
          columns: { tag: string; title: string; body: string }[];
        };
        process: {
          kicker: string;
          h2a: string;
          h2em: string;
          description: string;
          acts: { no: string; phase: string; title: string; body: string; steps: string[] }[];
        };
      };
      toolkit: {
        kicker: string;
        h2a: string;
        h2em: string;
        learnMore: string;
        cards: { title: string; body: string }[];
      };
      productPages: {
        ctaLine: string;
        dailyRhythm: { kicker: string; h2: string; lead: string };
        aiAssistant: { kicker: string; h2: string; lead: string };
        integrations: { kicker: string; h2: string; lead: string };
        goals: { kicker: string; h2: string; lead: string };
        journal: { kicker: string; h2: string; lead: string };
      };
      method: {
        kicker: string;
        headline: string;
        p1: string;
        p2: string;
        p3: string;
        close: string;
      };
      problem: {
        kicker: string;
        titleA: string;
        titleEm: string;
        description: string;
        solution: string;
      };
      day: {
        kicker: string;
        headline: string;
        tabs: { time: string; label: string }[];
        figLabel: string;
        sidebar: {
          today: string;
          archive: string;
          items: string[];
          archiveItems: string[];
        };
        mockDate: string;
        mockHeading: string;
        mockAi1: string;
        mockYou: string;
        mockAi2: string;
        mockWho: { ai: string; you: string };
        rightTitle: string;
        rightGoal: string;
        tasks: { label: string; meta: string }[];
        goalLabel: string;
        goalMeta: string;
        more: string;
      };
      compare: {
        kicker: string;
        headlineA: string;
        headlineEm: string;
        lead: string;
        featureHead: string;
        journailHead: string;
        competitors: string[];
        rows: { feature: string; values: boolean[] }[];
        note: string;
        dragHint: string;
        seeMore: string;
        allComparisons: string;
      };
      integrations: {
        kicker: string;
        headline: string;
        items: { name: string; desc: string }[];
        foot: string;
      };
      faq: {
        kicker: string;
        headlineA: string;
        headlineEm: string;
        items: { q: string; a: string }[];
      };
      pricing: {
        kicker: string;
        headlineA: string;
        headlineB: string;
        toggleMonthly: string;
        toggleYearly: string;
        toggleSave: string;
        perMonth: string;
        forever: string;
        noCard: string;
        billedYearly: string;
        billedMonthly: string;
        badgePopular: string;
        vatIncluded: string;
        plans: {
          eyebrow: string;
          title: string;
          priceYearly: string;
          priceMonthly: string;
          subYearly: string;
          subMonthly: string;
          cta: string;
          features: string[];
        }[];
        promise: { kicker: string; text: string; coda: string; sign: string };
        bridge: string;
      };
      archive: {
        kicker: string;
        headlineA: string;
        headlineEm: string;
        headlineB: string;
        leadA: string;
        leadB: string;
        leadC: string;
        soft: string;
        link: string;
        botDate: string;
        botPreview: string;
        hint: string;
        midDate: string;
        midPreview: string;
        midMeta: string;
        topDate: string;
        topClosed: string;
        topQuote: string;
        topPreview: string;
        topMeta: string;
        entries: Array<{
          date: string;
          quote?: string;
          preview: string;
          meta: string;
        }>;
      };
      founder: {
        kicker: string;
        paragraphs: string[];
        signName: string;
        signRole: string;
      };
      socialProof: {
        kicker: string;
        h2a: string;
        h2em: string;
        quotes: { text: string; author: string }[];
        founderTitle: string;
        founderLetter: string[];
        founderSignature: string;
      };
      finalCta: { kicker: string; line1: string; em: string; description: string; cta: string; micro: string };
      footerNav: {
        product: { heading: string; links: { label: string; href: string }[] };
        company: { heading: string; links: { label: string; href: string }[] };
        resources: { heading: string; links: { label: string; href: string }[] };
        legal: { heading: string; links: { label: string; href: string }[] };
        folio: string;
      };
    };
  };
  auth: {
    login: {
      title: string;
      subtitle: string;
      email: string;
      emailPlaceholder: string;
      password: string;
      submit: string;
      noAccount: string;
      register: string;
      error: string;
      pendingError: string;
      genericError: string;
      googleSignIn: string;
      forgotPassword: string;
    };
    register: {
      title: string;
      subtitle: string;
      subtitleHighlight: string;
      subtitleEnd: string;
      name: string;
      namePlaceholder: string;
      email: string;
      emailPlaceholder: string;
      password: string;
      passwordPlaceholder: string;
      submit: string;
      hasAccount: string;
      login: string;
      error: string;
      existsError: string;
      pendingTitle: string;
      pendingMessage: string;
      pendingBack: string;
    };
    forgot: {
      title: string;
      subtitle: string;
      email: string;
      emailPlaceholder: string;
      submit: string;
      sending: string;
      backToLogin: string;
      sentTitle: string;
      sentMessage: string;
      genericError: string;
    };
    reset: {
      title: string;
      subtitle: string;
      password: string;
      passwordPlaceholder: string;
      confirm: string;
      confirmPlaceholder: string;
      submit: string;
      saving: string;
      successTitle: string;
      successMessage: string;
      goToLogin: string;
      mismatch: string;
      invalidToken: string;
      requestNew: string;
      genericError: string;
    };
  };
  nav: {
    journal: string;
    archive: string;
    goals: string;
    tasks: string;
    calendar: string;
    faq: string;
    guides: string;
    privacy: string;
    terms: string;
    settings: string;
    admin: string;
    users: string;
    support: string;
    activity: string;
    logout: string;
    openMenu: string;
  };
  admin: {
    title: string;
    subtitle: string;
    stats: { users: string; entries: string; goals: string; pending: string };
    links: { users: string; support: string; logs: string };
    registrations: string;
    entriesChart: string;
    date: string;
    count: string;
    users: {
      title: string;
      subtitle: string;
      name: string;
      email: string;
      role: string;
      registered: string;
      roleAdmin: string;
      roleUser: string;
      rolePending: string;
      approve: string;
      roleChanged: string;
      roleChangeError: string;
      deleteUser: string;
      deleteConfirm: string;
      deleteSuccess: string;
      deleteError: string;
      deleteSelf: string;
      language: string;
      lastActivity: string;
      expandProfile: string;
    };
    logs: {
      title: string;
      subtitle: string;
      today: string;
      days7: string;
      days30: string;
      all: string;
      time: string;
      type: string;
      message: string;
      user: string;
      noLogs: string;
      types: Record<string, string>;
      filterByType: string;
      allTypes: string;
    };
  };
  journal: {
    title: string;
    greeting: string;
    todayPlan: string;
    startPlanning: string;
    continuePlanning: string;
    editPlan: string;
    finishDay: string;
    noPlan: string;
    startNew: string;
    planAhead: string;
    unfinalizedEntries: string;
    unfinalizedWarning: string;
    markAsDone: string;
    viewEntry: string;
    setupTitle: string;
    setupDescription: string;
    setupDescriptionCalendar: string;
    setupDescriptionEnd: string;
    settingsButton: string;
    unfinalizedTitle: string;
    unfinalizedNotFinished: string;
    unfinalizedHint: string;
    finishPrevious: string;
    entryExists: string;
    finalized: string;
    finishJournal: string;
    openInArchive: string;
    planAheadDesc: string;
    tomorrow: string;
    dayAfter: string;
    calendarEvents: string;
    tasksForDay: string;
    entryExistsWarning: string;
    editExistingPlan: string;
    loadingData: string;
    dataError: string;
    conversationError: string;
    todoistSkipped: string;
    tasksUpdated: string;
    tasksUpdateFailed: string;
    tasksUpdateError: string;
    entrySaved: string;
    entrySaveError: string;
    dayFinished: string;
    dayFinishError: string;
    refreshError: string;
    startButton: string;
    startButtonLoading: string;
    calendar: string;
    noEvents: string;
    allDay: string;
    tasks: string;
    addTask: string;
    taskPlaceholder: string;
    timePlaceholder: string;
    project: string;
    noProject: string;
    priorityUrgent: string;
    priorityHigh: string;
    priorityMedium: string;
    priorityNormal: string;
    create: string;
    priorityTasksSingular: string;
    priorityTasksPlural: string;
    priorityTasksMany: string;
    andMore: string;
    noTasks: string;
    overdue: string;
    forToday: string;
    completedToday: string;
    upcoming: string;
    activeGoals: string;
    goalYearly: string;
    goalMonthly: string;
    goalWeekly: string;
    tasksRefreshed: string;
    tasksRefreshError: string;
    taskCompleted: string;
    taskReopened: string;
    taskUpdateError: string;
    taskDeleted: string;
    taskDeleteError: string;
    taskContentRequired: string;
    taskCreated: string;
    taskCreateError: string;
    deleteTask: string;
    // AIConversation phase labels
    phases: {
      priorities: string;
      taskSelection: string;
      myPlan: string;
      completed: string;
      taskReview: string;
      comment: string;
      journal: string;
      editPlan: string;
      todoist: string;
    };
    // AIConversation UI
    continueToPhase: string;
    jumpTo: string;
    inputPlaceholder: string;
    planRefinePlaceholder: string;
    conversationDone: string;
    finalizingDay: string;
    finalizeDay: string;
    openInArchiveBtn: string;
    editText: string;
    cancelEdit: string;
    submitEdits: string;
    confirmAndSave: string;
    saving: string;
    confirmAndContinue: string;
    confirming: string;
    saveAndFinish: string;
    saveEntry: string;
    editBtn: string;
    todayEntry: string;
    taskReviewTitle: string;
    taskReviewSubtitle: string;
    refresh: string;
    noTasksForToday: string;
    optionalNote: string;
    notePlaceholder: string;
    continueToReview: string;
    whichTasksToday: string;
    overdueLabel: string;
    todayLabel: string;
    noTasksToSelect: string;
    taskContentPlaceholder: string;
    projectLabel: string;
    dueDateToday: string;
    dueDateTomorrow: string;
    dueDateWeekend: string;
    dueDateNextWeek: string;
    addBtn: string;
    highlightsTitle: string;
    highlightsSubtitle: string;
    highlightsPlaceholder: string;
    continueToMyPlan: string;
    phaseCompleted: string;
    aiError: string;
    taskReviewError: string;
    tasksCompleted: string;
    cancelDelete: string;
    deleteBtn: string;
    rescheduleKeep: string;
    rescheduleTomorrow: string;
    rescheduleWeekend: string;
    rescheduleNextWeek: string;
    selectAll: string;
    deselectAll: string;
    suggestionsFromGoals: string;
    addNewTask: string;
  };
  archive: {
    title: string;
    subtitle: string;
    export: string;
    noEntries: string;
    finalized: string;
    draft: string;
    loadMore: string;
    noEntriesMessage: string;
    startJournalHint: string;
    searchPlaceholder: string;
    searchNoResults: string;
    searchResultsCount: string;
  };
  goals: {
    title: string;
    subtitle: string;
    weekly: string;
    monthly: string;
    yearly: string;
    addGoal: string;
    noGoals: string;
    status: { active: string; completed: string; partial: string; failed: string };
    yearlyGoals: string;
    monthlyGoals: string;
    weeklyGoals: string;
    add: string;
    noGoalsEmpty: string;
    noActiveGoals: string;
    showArchived: string;
    hideArchived: string;
    archivedGoals: string;
  };
  settings: {
    title: string;
    profile: {
      sectionTitle: string;
      title: string;
      description: string;
      nameLabel: string;
      namePlaceholder: string;
      emailLabel: string;
      emailHint: string;
      avatarHint: string;
      save: string;
      saved: string;
      errorSave: string;
      loading: string;
    };
    integrations: string;
    email: string;
    absences: string;
    language: string;
    languageDescription: string;
    uiLanguage: string;
    uiLanguageDescription: string;
    contentLanguage: string;
    contentLanguageDescription: string;
    contentLanguagePlaceholder: string;
    taskProvider: {
      title: string;
      description: string;
      internal: string;
      internalDescription: string;
      todoistLabel: string;
      todoistDescription: string;
      googleTasks: string;
      googleTasksDescription: string;
      switchWarning: string;
      saved: string;
      saveError: string;
      projects: string;
      addProject: string;
      projectName: string;
      noProjects: string;
      projectAdded: string;
      projectDeleted: string;
      notImplemented: string;
    };
    todoist: {
      description: string;
      connected: string;
      disconnected: string;
      connectedMessage: string;
      disconnect: string;
      apiKey: string;
      apiKeyPlaceholder: string;
      apiKeyHelp: string;
      connect: string;
      connectSuccess: string;
      connectError: string;
      disconnectSuccess: string;
      disconnectError: string;
    };
    calendar: {
      description: string;
      disconnected: string;
      addAccount: string;
      connectCalendar: string;
      loadingAccounts: string;
      selectCalendars: string;
      saveSelection: string;
      noCalendarSelected: string;
      calendarsCount: string;
      connectError: string;
      accountRemoved: string;
      removeError: string;
      calendarsSaved: string;
      saveError: string;
      verificationTitle: string;
      verificationBody: string;
    };
    emailNotifications: {
      title: string;
      description: string;
      emailAddress: string;
      morningEmail: string;
      morningDescription: string;
      sendAt: string;
      eveningReminder: string;
      eveningDescription: string;
      receiveWeekends: string;
      saved: string;
      saveError: string;
    };
    absence: {
      title: string;
      description: string;
      from: string;
      to: string;
      reason: string;
      reasonPlaceholder: string;
      add: string;
      added: string;
      removed: string;
      dateError: string;
      saveError: string;
      deleteError: string;
    };
    workHours: {
      sectionTitle: string;
      title: string;
      description: string;
      startLabel: string;
      endLabel: string;
      timezoneLabel: string;
      timezoneHint: string;
      detectTz: string;
      save: string;
      saved: string;
      loading: string;
      errorRange: string;
      errorSave: string;
    };
    billing: {
      title: string;
      freePlan: string;
      proPlan: string;
      premiumPlan: string;
      statusActive: string;
      statusPastDue: string;
      statusCancelled: string;
      statusExpired: string;
      statusTrial: string;
      activeUntil: string;
      cancelsOn: string;
      pastDueNote: string;
      manage: string;
      invoices: string;
      cancelSub: string;
      cancelConfirm: string;
      cancelTrial: string;
      cancelTrialConfirm: string;
      cancelTrialDone: string;
      noSubHint: string;
      changePlan: string;
      upgradeCta: string;
      switchCta: string;
      currentPlanBadge: string;
      vatIncluded: string;
      paymentFailedHelp: string;
      loadingStatus: string;
      successToast: string;
      cycleMonthly: string;
      cycleYearly: string;
    };
    changePassword: {
      sectionTitle: string;
      title: string;
      description: string;
      current: string;
      currentPlaceholder: string;
      new: string;
      newPlaceholder: string;
      confirm: string;
      confirmPlaceholder: string;
      submit: string;
      saving: string;
      saved: string;
      errorCurrent: string;
      errorMismatch: string;
      errorShort: string;
      errorGeneric: string;
      noPasswordHint: string;
    };
    dataPrivacy: {
      sectionTitle: string;
      exportTitle: string;
      exportDescription: string;
      exportButton: string;
      exporting: string;
      exportError: string;
    };
    deleteAccount: {
      sectionTitle: string;
      title: string;
      description: string;
      warning: string;
      exportHint: string;
      button: string;
      confirmPhrase: string;
      confirmPrompt: string;
      passwordLabel: string;
      passwordPlaceholder: string;
      confirmButton: string;
      cancel: string;
      activeSubError: string;
      invalidPassword: string;
      genericError: string;
      deleting: string;
    };
  };
  support: {
    title: string;
    newTicket: string;
    myMessages: string;
    subject: string;
    subjectPlaceholder: string;
    message: string;
    messagePlaceholder: string;
    send: string;
    sent: string;
    sendError: string;
    replyError: string;
    noMessages: string;
    createNew: string;
    open: string;
    resolved: string;
    markResolved: string;
    reopen: string;
    statusChanged: string;
    statusError: string;
    writeReply: string;
    floatingButton: string;
    user: string;
    adminLabel: string;
  };
  notifications: {
    title: string;
    markAllRead: string;
    noNotifications: string;
  };
  emails: {
    journalEntry: string;
    morningPlan: string;
    eveningReminder: string;
    openJournal: string;
    finishJournal: string;
    startJournal: string;
    completedTasks: string;
    overdue: string;
    today: string;
    tasksForToday: string;
    noOpenTasks: string;
    autoGenerated: string;
    important: string;
    entryExists: string;
    noEntry: string;
    newUserRegistered: string;
    accountApproved: string;
    accountApprovedMessage: string;
    loginNow: string;
    signoffMorning: string;
    signoffEvening: string;
    signoffJournal: string;
    plan: string;
    summary: string;
    eyebrowMorning: string;
    eyebrowEvening: string;
    eyebrowJournal: string;
    eyebrowApproval: string;
    eyebrowAdmin: string;
    eyebrowReset: string;
    passwordResetTitle: string;
    passwordResetMessage: string;
    passwordResetExpiry: string;
    passwordResetIgnore: string;
    resetCta: string;
    adminPendingHeading: string;
    adminPendingNameLabel: string;
    adminPendingDateLabel: string;
    adminOpenPanel: string;
    eyebrowTrial: string;
    trialStartedTitle: string;
    trialStartedMessage: string;
    trialOpenCta: string;
    trialReminderTitle: string;
    trialReminderDays: string;
    trialReminderMessage: string;
    trialReminderCta: string;
    trialEndedTitle: string;
    trialEndedMessage: string;
    trialEndedCta: string;
    adminSignupHeading: string;
    footerNote: string;
    unsubscribeNote: string;
    settingsLink: string;
    settingsLinkLabel: string;
  };
  consent: {
    banner: {
      title: string;
      description: string;
      acceptAll: string;
      rejectAll: string;
      customize: string;
      saveSelection: string;
      noticeTitle: string;
      noticeDescription: string;
      doNotSell: string;
    };
    categories: {
      necessary: { title: string; description: string };
      functional: { title: string; description: string };
      analytics: { title: string; description: string };
      marketing: { title: string; description: string };
    };
    settings: {
      title: string;
      subtitle: string;
      manage: string;
      currentChoices: string;
      lastUpdated: string;
      privacyLink: string;
    };
    admin: {
      title: string;
      subtitle: string;
      version: string;
      filterRegion: string;
      filterAll: string;
      time: string;
      subject: string;
      user: string;
      region: string;
      country: string;
      event: string;
      categories: string;
      gpc: string;
      empty: string;
    };
    footer: {
      privacy: string;
      terms: string;
      cookies: string;
      preferences: string;
    };
  };
  composer: {
    modeSwitch: {
      eyebrow: string;
      /** "{toLabel}" placeholder. */
      titleTo: string;
      /** "{fromLabel}" + "{toLabel}" placeholders. */
      bodyFromTo: string;
      /** "{fromLabel}" placeholder. */
      cancelKeep: string;
      confirm: string;
      modePlan: string;
      modeReflect: string;
      modeAsk: string;
    };
    slash: {
      taskAdd: string;
      taskEdit: string;
      taskComplete: string;
      taskDelete: string;
      taskMove: string;
      taskCompleteSoon: string;
      journalSearch: string;
      note: string;
      noteAdded: string;
      noteUnavailable: string;
      today: string;
      yesterday: string;
      yesterdayMissing: string;
      help: string;
      helpToast: string;
      planTomorrow: string;
      sectionTasks: string;
      sectionJournal: string;
      sectionQuick: string;
      unknown: string;
    };
    undo: {
      /** "{content}" placeholder. */
      taskAdded: string;
      label: string;
    };
    ui: {
      contextAttached: string;
      noContextYet: string;
      quickReply: string;
      quickReplies: string;
      orWriteYourOwn: string;
      send: string;
      newline: string;
      commands: string;
      stop: string;
      thinking: string;
      navigate: string;
      select: string;
      complete: string;
      cancel: string;
    };
  };
  onboarding: {
    ariaLabel: string;
    eyebrow: string;
    heading: { plain: string; em: string; suffix: string };
    subtitle: string;
    dismiss: string;
    /** "{n}" and "{total}" placeholders, e.g. "Korak {n} od {total}" */
    stepLabel: string;
    /** "{total}" placeholder */
    showAll: string;
    hideAll: string;
    currentBadge: string;
    markDone: string;
    markNotDone: string;
    allDoneHeading: { em: string; suffix: string };
    steps: {
      setLanguages: { title: string; cta: string; hint: string };
      firstGoal: { title: string; cta: string; hint: string };
      calendar: { title: string; cta: string; hint: string };
      taskSource: { title: string; cta: string; hint: string };
      sideChat: { title: string; cta: string; hint: string };
      firstPlanning: { title: string; cta: string; hint: string };
      firstEntry: { title: string; cta: string; hint: string };
    };
  };
  trialGate: {
    title: string;
    subtitle: string;
    signOut: string;
    publicSite: string;
  };
}

export const sl: Translations = {
  common: {
    loading: "Nalagam...",
    save: "Shrani",
    cancel: "Prekliči",
    delete: "Izbriši",
    edit: "Uredi",
    back: "Nazaj",
    next: "Naprej",
    confirm: "Potrdi",
    send: "Pošlji",
    sending: "Pošiljam...",
    search: "Iskanje",
    noData: "Ni podatkov",
    error: "Napaka",
    success: "Uspešno",
    close: "Zapri",
    yes: "Da",
    no: "Ne",
    or: "ali",
    and: "in",
    all: "Vse",
    none: "Brez",
    previous: "Prejšnja",
    nextPage: "Naslednja",
    page: "Stran",
    of: "od",
    justNow: "pravkar",
    minutesAgo: "min",
    hoursAgo: "h",
    daysAgo: "d",
  },
  landing: {
    hero: {
      tagline: "Tvoj AI partner za dnevno planiranje",
      description: "Načrtuj dan s pomočjo AI, spremljaj napredek pri ciljih in si ustvari dnevnik, ki ti omogoča pregled dosežkov in aktivnosti po posameznih dnevih.",
      cta: "Začni zdaj",
      login: "Prijava",
      register: "Registracija",
      registerNote: "Dostop bo dodeljen po odobritvi administratorja.",
      betaBadge: "V fazi testiranja - brezplačno",
      openApp: "Odpri aplikacijo",
    },
    features: {
      title: "Funkcionalnosti",
      subtitle: "Vse kar potrebuješ za učinkovit dan",
      goals: {
        title: "Hierarhični cilji",
        description: "Letni, mesečni in tedenski cilji z avtomatičnim sledenjem napredka. Poveži dnevne naloge s širšimi cilji.",
        badge: "Edinstveno",
      },
      ai: {
        title: "AI načrtovanje dneva",
        description: "Inteligentni asistent, ki ti pomaga načrtovati dan na podlagi tvojih ciljev, nalog in koledarskih dogodkov.",
      },
      todoist: {
        title: "Todoist integracija",
        description: "Sinhroniziraj naloge neposredno s Todoistom. Prenesi, uredi in dodajaj naloge brez zapuščanja aplikacije.",
      },
      calendar: {
        title: "Google Koledar",
        description: "Preglej koledarske dogodke in jih vključi v dnevni plan. Nikoli ne zamudi sestanka.",
      },
      archive: {
        title: "Arhiv in timeline",
        description: "Preglej pretekle vnose na časovnici. Izvozi dnevnik v markdown formatu.",
      },
      email: {
        title: "Jutranji in večerni email",
        description: "Prejmi jutranji pregled nalog in večerni opomnik za zaključek dneva neposredno na email.",
      },
      absences: {
        title: "Upravljanje odsotnosti",
        description: "Označi dni dopusta ali odsotnosti. Avtomatično prilagodi email obvestila.",
      },
    },
    howItWorks: {
      title: "Kako deluje",
      subtitle: "6 korakov do produktivnega dneva",
      steps: [
        { title: "Prioritete", description: "Povej AI-ju kaj bi danes rad delal in kaj je pomembno." },
        { title: "Pregled", description: "AI analizira tvoje naloge, cilje in koledar ter pripravi predloge." },
        { title: "Izbira nalog", description: "Izberi naloge za danes iz Todoist seznama in AI predlogov." },
        { title: "Dnevni plan", description: "AI sestavi dnevni plan in sinhronizira spremembe s Todoistom." },
        { title: "Večerni pregled", description: "Ob koncu dneva preglej opravljeno in zapiši refleksijo." },
        { title: "Dnevnik", description: "AI sestavi dnevniški vnos na podlagi celotnega pogovora." },
      ],
    },
    footer: {
      copyright: "Journail. Vse pravice pridržane.",
    },
    cover: {
      edgeLeft: "Journail · MMXXVI",
      edgeRight: "Številka 01 · Naslovnica",
      nav: {
        product: "Produkt",
        method: "Metoda",
        pricing: "Cenik",
        manifesto: "Manifest",
        changelog: "Novosti",
        faq: "Pogosta vprašanja",
        blog: "Blog",
        signIn: "Prijava",
        startTrial: "Začni brezplačno",
        guides: "Vodniki",
        productMenu: [
          { label: "Dnevni ritem", desc: "Tvoj 4-stopenjski cikel", href: "/product/daily-rhythm" },
          { label: "AI pomočnik", desc: "Kako AI deluje", href: "/product/ai-assistant" },
          { label: "Cilji", desc: "Od letnih do tedenskih, usklajeno", href: "/product/goals" },
          { label: "Dnevnik", desc: "Piše se sam iz tvojega dne", href: "/product/journal" },
          { label: "Integracije", desc: "Google Calendar in Todoist", href: "/product/integrations" },
        ],
        guidesMenu: [
          { label: "Navodila", desc: "Kratki triki in bližnjice", href: "/guides/tutorials" },
          { label: "Naša sled", desc: "Kaj se je spremenilo, verzijo za verzijo", href: "/guides/our-trail" },
          { label: "Zgodbe", desc: "Članki o refleksiji in fokusu", href: "/blog" },
          { label: "Pogosta vprašanja", desc: "Tehnična in produktna vprašanja", href: "/faq" },
        ],
      },
      hero: {
        kicker: "// USKLAJENI KROG",
        headline: {
          line1A: "Umirjen",
          line1Em: "planer zjutraj",
          line1B: ".",
          line2A: "Premišljen",
          line2Em: "dnevnik zvečer",
          line2B: ".",
        },
        purposeLine: "AI dnevni planer & dnevnik",
        description:
          "Pametni dnevni planer in dnevnik v enem — AI planer za vsak dan. Nehaj gasiti nujne požare. Spravi svoje dolgoročne cilje in dnevni fokus v **popolno usklajenost**. Vzemi si le 5 minut na dan, da z vodeno AI pomočjo zapišeš svoj jutranji načrt in **z lahkoto** preliješ svoje misli v večerni dnevnik. Dnevni planer in dnevnik, ki rasteta s tabo.",
        descriptionShort:
          "Pameten dnevni planer in dnevnik v enem. Zjutraj načrtuj, zvečer zajemi — 5 minut na dan, vodeno z AI.",
        ctaPrimary: "Začni brezplačni preizkus",
        ctaSecondary: "Kako deluje",
        trust: [
          "7-dnevni preizkus.",
          "Nato €7/mesec.",
          "Kreditna kartica ni potrebna.",
          "Tvoji podatki nikoli ne trenirajo AI modelov.",
        ],
        calendarNote: "Google Koledar je neobvezen. Journail le prebere tvoje dogodke in doda tiste, ki jih razporediš. Prekličeš kadarkoli.",
        fig: "SL. 01",
        figLabel: "Dnevni ritem",
        figSub: "Dan, ki te vleče k bistvenemu.",
        nodes: [
          { code: "6:30", label: "Dnevni plan" },
          { code: "9:00", label: "Potek dneva" },
          { code: "18:30", label: "Dnevniški zapis" },
          { code: "22:00", label: "Priprava za jutri", optional: true, optionalLabel: "neobvezno" },
        ],
        goalsLabel: "GRAVITACIJA CILJEV",
        goalsSub: "LETA · MESECI · TEDNI",
      },
      chapters: [
        {
          no: "01 / DNEVNI NAČRT",
          title: "Načrtuj dan v petih minutah.",
          body: "AI pregleda tvoje cilje, koledar in naloge ter predlaga dve opravili — ne dvajsetih.",
        },
        {
          no: "02 / FOKUS",
          title: "Tvoj fokus, ki se prilagaja.",
          body: "Mirna časovnica, ki te spremlja ves dan. Odkljukaj naloge, dodaj misli ali vprašaj AI. Brez motenj.",
        },
        {
          no: "03 / DNEVNIK",
          title: "Razumi dan v dveh minutah.",
          body: "Refleksija o tem, kaj se je premaknilo in kje ustavilo. Skozi kratek pogovor AI ujame tvoje misli v dnevniški zapis, shranjen z tvojim unikatnim glasom.",
        },
        {
          no: "04 / PRIPRAVA NA JUTRI",
          title: "Za miren začetek jutra.",
          body: "Opcijsko: Svoj dnevni načrt lahko opraviš že nocoj. Prenesi namere iz glave v Journail, da se zbudiš z jasnim fokusom.",
        },
      ],
      showcase: {
        kicker: "// KAKO DELUJE",
        h2: "En cikel. Manj kot 5 minut na dan.",
        learnMore: "Spoznaj dnevni ritem",
        phases: [
          {
            no: "01 / Dnevni plan",
            title: "Iz praznega platna do popolne uskladitve.",
            body: "Ko začneš svoj dan, Journail preveri tvoje dolgoročne cilje in čakajoče naloge ter sestavi uravnotežen načrt za danes. Deluje kot ščit, ki poskrbi, da operativne malenkosti nikoli ne zasenčijo tvojih velikih mejnikov.",
          },
          {
            no: "02 / Potek dneva",
            title: "Tvoje mirno sidrišče za sprotni fokus.",
            body: "Ko je načrt postavljen, Journail postane tvoj osrednji prostor čez dan. Zapiraj opravljene naloge, sproti dodajaj nove ali si zapiši hitre opombe, preden pobegnejo. Želiš analizirati pretekle vzorce? Uporabi stranskega AI pomočnika, da v naravnem jeziku pregleda tvojo zgodovino — vse znotraj enega samega zavihka v brskalniku.",
          },
          {
            no: "03 / Dnevniški zapis",
            title: "Pretopi dnevni trud v trajen spomin.",
            body: "Ob koncu dneva poglej svoj napredek. Če si na kaj res ponosen, kar ti je uspelo ustvariti, to preprosto zapiši. Journailov AI ga nato avtomatsko združi s tvojimi opravljenimi nalogami in opombami v čudovito urejen dnevniški vnos. Tvoj dan se zapre, tvoja osebna rast pa se shrani za vedno.",
          },
          {
            no: "04 / Priprava za jutri",
            title: "Odloži skrbi pred spanjem.",
            body: "Izbiren večerni ritual za vse, ki želijo zaspati s popolnoma prazno glavo. V nekaj besedah skiciraj ključni fokus za jutri, zapri prenosnik in si spočij z vedenjem, da je vse varno zabeleženo.",
          },
        ],
        screens: {
          plan: {
            date: "PON · 8. JUNIJ",
            title: "Današnji plan",
            tasks: [
              { title: "Osnutek mesečne strategije", time: "09:00" },
              { title: "Globok fokus: lansiranje", time: "10:30" },
              { title: "Pregled s ekipo", time: "14:00" },
              { title: "Tek ob reki", time: "18:00" },
            ],
          },
          flow: {
            date: "PON · 13:42",
            title: "Današnji fokus",
            tasks: [
              { title: "Osnutek mesečne strategije", time: "09:00" },
              { title: "Globok fokus: lansiranje", time: "10:30" },
              { title: "Pregled s ekipo", time: "14:00" },
            ],
            focus: "FOKUS · 52 MIN",
          },
          journal: {
            date: "PON · ZAKLJUČEK DNEVA",
            title: "Današnji dnevnik",
            highlight: "Strategija končno sedla na svoje mesto.",
            body: "Tri globoke ure dela, brez prekinitev. Pregled s ekipo je odprl smer, ki je prej nisem videl.",
            reflection: "Najboljše delo nastane, ko jutro ostane neokrnjeno.",
          },
          tomorrow: {
            date: "JUTRI · TOR",
            title: "Priprava za jutri",
            intentions: [
              "Dokončaj predstavitev pred kosilom.",
              "Ena ura brez zaslonov zvečer.",
            ],
            note: "Dobro počivaj. →",
          },
        },
      },
      journalSection: {
        kicker: "// DNEVNIK",
        h2a: "Tvoja preteklost je",
        h2em: "tvoj najboljši učitelj.",
        description: "Mozaik tvojih trenutkov. Poglej nazaj, da stopiš naprej. Medtem ko navadni planerji tvojo zgodovino izbrišejo takoj, ko odkljukaš nalogo, Journail tvojo pot ohrani. Vsak opravljen mejnik, vsaka hitra opomba in vsak trenutek ponosa so vtkani v čudovito urejen arhiv.",
        growth: "Tvoj dnevnik ni le skladišče preteklosti — je tvoj največji učitelj. Ko se ozreš nazaj na svojo sled, odkriješ svoje dejanske vzorce produktivnosti, ugotoviš, kaj te ovira, in iz tedna v teden zavestno izboljšuješ svoj fokus. Pretopi vsakodnevni trud v modrost, ki se stopnjuje skozi čas.",
        annotationTag: "PONOSEN TRENUTEK",
        annotationLabel: "Nekaj, na kar sem ponosen.",
        learnMore: "Več o dnevniku",
        cardDate: "Sreda · 6. maj 2026",
        cardEntry: "Prvi cel blok globokega dela po dveh tednih.",
        cardBody: "Miren dan. Globok delovni blok na memorandumu za upravo in dolg sprehod po kosilu. Vrnil sem se v čisto popoldne.",
      },
      assistant: {
        kicker: "AI POMOČNIK",
        h2a: "Sopotnik, ki te dejansko",
        h2em: "pozna.",
        learnMore: "Spoznaj AI pomočnika",
        description: "Journail prinaša integriranega stranskega pomočnika, ki ne le obdeluje besedila — ampak razume tvoje življenje. S polnim kontekstom tvojih dolgoročnih ciljev in zadnjih 14 dni zapisov deluje kot osebni strateg, osveževalec spomina in bliskoviti urejevalnik v enem umirjenem vmesniku.",
        ctaIntro: "Preizkusi pomočnika v živo. Klikni na spodnje ukaze in poglej, kako deluje:",
        commands: [
          "/task Preglej tekst za naslovnico jutri ob 10:00",
          "Kaj me je ta teden najbolj oviralo pri fokusu?",
          "Povzemi moje ključne dosežke v juniju",
        ],
        demoYou: "Ti",
        demoThinking: "Journail razmišlja…",
        demoIdle: "Klikni ukaz na levi in poglej pomočnika v akciji.",
        demoReplayHint: "Klikni drug ukaz za nov primer.",
        demoScrollHint: "Drsaj naprej ↓",
        demoUnderstood: "Razumem. Takole sem to razumel:",
        demoConfirmLabel: "Prosim potrdi s klikom:",
        demoChipVerb: "Dodaj nalogo",
        demoChipTip: "Jutri ob 10:00 · povezal jo bom s tvojim ciljem »Lansiranje«.",
        demoTaskTitle: "Preglej tekst za naslovnico",
        demoTaskWhen: "Jutri · 10:00",
        demoResponses: [
          "Narejeno — naloga je dodana v jutrišnji plan in usklajena s tvojim ciljem »Lansiranje«.",
          "Pogled na zadnjih 7 dni: najgloblji fokus si imel dopoldne, pred 11:00. Tri popoldneve so razbile nenačrtovane administrativne naloge — tam se je zalomil zagon. Dva ključna cilja nista dobila svojega bloka. Naslednji teden zaščiti eno dopoldne za svoj največji cilj.",
          "Junij na kratko: zaključil si 18 od 22 načrtovanih nalog, ohranil 12-dnevni niz pisanja dnevnika in stvarno napredoval pri dveh od treh četrtletnih ciljev. Tvoj najponosnejši trenutek: dokončanje projekta, ki si ga dvakrat prelagal. Osredotočen, miren mesec.",
        ],
        demoLabel: "DEMO",
        scrollCue: "SKROLAJ ZA PREIZKUS",
        closerA: "Manj klepetalnik. Bolj ",
        closerEm: "spomin, ki misli s teboj.",
        steps: [
          {
            node: "Ustvari",
            meta: "01 / Ustvari opravilo",
            ctx: "Prebere tvoje cilje · ustvari opravilo",
            slash: "/task ",
            cmd: "Preglej tekst za naslovnico jutri ob 10:00",
            tag: "Opravilo ustvarjeno",
            ai: "Dodano za jutri ob 10:00 in povezano s tvojim ciljem »Lansiranje«. Naj rezerviram 45 minut okrog tega?",
          },
          {
            node: "Razmisli",
            meta: "02 / Razmisli o tednu",
            ctx: "Prebere zadnjih 14 dni · najde vzorce",
            slash: "",
            cmd: "Kaj me je ta teden oviralo pri fokusu?",
            tag: "Vzorec najden",
            ai: "Trije dnevi izgubljeni zaradi nenačrtovanih sestankov. Tvoji bloki za poglobljeno delo so zdržali le v torek in četrtek — oba dopoldneva.",
          },
          {
            node: "Povzemi",
            meta: "03 / Povzemi mesec",
            ctx: "Prebere opravila, cilje in dnevnik · povzame",
            slash: "",
            cmd: "Povzemi moje ključne dosežke v juniju",
            tag: "Povzetek junija",
            ai: "Prenovljena naslovnica, zaprta 2 mesečna cilja in 11 zapisov v dnevniku. Najmočnejši teden: 8.–12. jun.",
          },
        ],
      },
      languageSection: {
        kicker: "// JEZIKOVNA SVOBODA",
        h2a: "Angleški vmesnik. Srce, ki govori",
        h2em: "tvoj jezik.",
        description:
          "Čeprav je osnovni vmesnik Journaila v čisti, profesionalni angleščini, tvojim mislim ni treba biti. Svoje naloge, hitre opombe in dnevniške zapise lahko pišeš v jeziku, v katerem se počutiš najbolj domače.",
        settingsNote:
          "V nastavitvah lahko natančno določiš, v katerem jeziku želiš komunicirati s svojim AI pomočnikom. Želiš klepetati v slovenščini, nemščini ali španščini? AI bo nemoteno pregledal tvojo zgodovino, odgovarjal na tvoja vprašanja in ustvaril tvoj večerni dnevniški zapis v jeziku, ki si ga izbral. Prava uskladitev ne pozna meja.",
        mockBreadcrumb: "NASTAVITVE",
        mockLabel: "Jezik vsebine",
        mockHint: "100+ jezikov",
      },
      aiAssistantPage: {
        commands: {
          kicker: "// NARAVNI JEZIK",
          h2a: "Od misli do izvedbe",
          h2em: "v nekaj sekundah.",
          description:
            "Dva načina, en rezultat. Vtipkaj hiter ukaz ali pa preprosto napiši v popolnoma normalnem jeziku — asistent razbere tvojo namero, jo razreši v nalogo (rok, projekt, cilj) in ti pokaže potrditveni chip. Nič ne nastane, dokler ne klikneš.",
          naturalLabel: "Piši naravno",
          naturalInput:
            "Add task Prepare proposal for ACME with due date beginning next week in project Sales and goal Revenue plan for Q3.",
          slashLabel: "… ali hiter ukaz",
          slashInput: "/task Prepare proposal for ACME · next week | project: Sales | goal: Revenue Q3",
          understood: "Asistent je razbral — potrdi:",
          chipVerb: "Dodaj nalogo",
          chipDetail: "Prepare proposal for ACME · zač. nasl. tedna · projekt: Sales · cilj: Revenue Q3",
          confirmHint: "Nič ne nastane, dokler chipa ne klikneš za potrditev.",
          actionsLead: "Ne le ustvarjanje —",
          actions: ["Dodaj", "Uredi", "Zaključi", "Izbriši", "Premakni"],
          actionsTail: "naloge, cilje in dogodke.",
        },
        memory: {
          kicker: "// VEDNO V KONTEKSTU",
          h2a: "Tvoji cilji in nedavni dnevi,",
          h2em: "vedno v kontekstu.",
          description:
            "Generični AI modeli pozabijo, kdo si, takoj ko zapreš zavihek. Journailov pomočnik pa vedno nosi celotno sliko — vsak aktiven cilj, ki ga zasleduješ, in refleksije tvojih nedavnih dni. Vprašaj in poveže pike, ne da bi mu moral znova razlagati svoje življenje.",
          pills: ["Vsi aktivni cilji", "Nedavne refleksije", "Odprte naloge"],
        },
        strategy: {
          kicker: "// STRATEŠKI POGOVOR",
          h2a: "Poglej svoje vzorce.",
          h2em: "Načrtuj svojo rast.",
          description:
            "Tvoji zapisi niso le pasivno besedilo — so signal. Vprašaj pomočnika, kaj deluje in kaj te vleče s poti, in odgovori ti iz tvojih ciljev in nedavnih refleksij.",
          dialogue: [
            {
              user: "Pri mojih aktivnih ciljih — kje izgubljam zagon?",
              ai: "Razvoj produkta je na dobri poti. A tvoje refleksije v tem tednu omenjajo nepričakovano podporno delo, ki je na treh dneh izrinilo globok fokus. Naj ti za naslednji teden zaščitim dopoldanski blok za tvoj glavni cilj?",
            },
            {
              user: "Kaj mi zadnje čase najbolj odžira fokus?",
              ai: "Tvoje nedavne večerne refleksije kažejo padec fokusa prav na dneve z zaporednimi dopoldanskimi sestanki. Najbolj ponosen si bil ob dnevih, ko si prve ure namenil globokemu delu.",
            },
          ],
        },
      },
      dailyRhythmPage: {
        anatomy: {
          kicker: "// DELOVNO OKOLJE",
          h2a: "En zaslon,",
          h2em: "trije mirni stolpci.",
          description:
            "Journail ni nadzorna plošča gradnikov, ki se borijo za pozornost. Je eno umirjeno delovno okolje: tvoj ritem na levi, tvoj pogovor na sredini, tvoje naloge in cilji na desni.",
          columns: [
            {
              tag: "LEVO · ZANKA",
              title: "Kje v dnevu si",
              body: "Zanka s štirimi vozlišči kaže trenutno fazo, z aktivnim korakom, ki nežno utripa. Pod njo prihajajoči dogodki, še niže pa nedavni poudarki — trenutki, na katere si bil ponosen v zadnjih dnevnikih.",
            },
            {
              tag: "SREDINA · NIT",
              title: "Pogovor z AI pomočnikom",
              body: "Osrednji stolpec je nit, ne obrazec. Tu se odvija celoten dan — jutranji brief, izbira nalog, plan, fokus in večerni zaključek — z eno glavno akcijo in vnosnim poljem na dnu.",
            },
            {
              tag: "DESNO · NALOGE IN CILJI",
              title: "Tvoj kontekst pri roki",
              body: "Najprej aktivne naloge s prioriteto in oznako — pa naj živijo v Journailu, Todoistu ali Google Koledarju. Pod njimi tvoji dolgoročni cilji, ki se berejo kot proza, ne kot ploščice.",
            },
          ],
        },
        process: {
          kicker: "// CELOTEN DAN",
          h2a: "Kako teče en dan,",
          h2em: "od jutra do zapisa.",
          description:
            "Vse se zgodi v osrednji niti. Zjutraj v nekaj minutah skupaj sestavita plan, čez dan delaš in se pogovarjaš, zvečer pa se dan sam pretopi v dnevniški zapis. Spodaj cel proces, korak za korakom.",
          acts: [
            {
              no: "01",
              phase: "JUTRO · PLAN",
              title: "Plan sestavita v kratkem pogovoru.",
              body: "Planiranje ni izpolnjevanje obrazca — je voden pogovor, kjer asistent prinese kontekst, ti pa odločaš.",
              steps: [
                "Dnevni brief te pozdravi z dvema vprašanjema: kaj je danes res pomembno in kaj ti tiho leži.",
                "Najprej poveš, kaj si že naredil oziroma kaj je tvoja glavna prioriteta za danes.",
                "Sledi izbor: asistent predlaga naloge iz planerja, dodatne predloge glede na tvoje cilje in koledar — ti izbereš, kaj boš delal, lahko dodaš svoje, ostalo prestaviš na jutri.",
                "Iz izbranega ti pripravi plan. Lahko mu kaj napišeš, plan urejaš ali ga potrdiš. S potrditvijo je narejen plan za danes in stanje se prestavi v Fokus.",
              ],
            },
            {
              no: "02",
              phase: "DAN · FOKUS",
              title: "Čez dan le delaš.",
              body: "Ko je plan postavljen, postane delovno okolje tvoje pristanišče za ves dan.",
              steps: [
                "Današnji plan stoji pred tabo — zaključuj naloge in spremljaj napredek v odstotkih.",
                "Pogovarjaj se z AI pomočnikom, dodajaj nove naloge ali zapiši hitro opombo, preden pobegne.",
                "Vsak zaključek in opomba se tiho nizata v nit kot zapis dneva. Ko si pripravljen, pritisneš »Zaključi dan«.",
              ],
            },
            {
              no: "03",
              phase: "VEČER · ZAPIS",
              title: "Dan se pretopi v dnevnik.",
              body: "Ob koncu dneva sprožiš pripravo dnevniškega zapisa — voden zaključek v treh korakih.",
              steps: [
                "Najprej pregled nalog: označiš opravljene, na odprtih zapišeš opombo, kaj še manjka.",
                "Asistent pripravi osnutek zapisa — opravljene naloge, povezave s cilji in povzetek. Ti ga potrdiš ali urejaš.",
                "Izbirno dopišeš, kaj je bilo danes res pomembno. Zapis je shranjen v tvojo časovnico — »Današnja zgodba je zapisana.« Jutri odpreš novo stran.",
              ],
            },
          ],
        },
      },
      toolkit: {
        kicker: "// ZAUPANJE IN PRILAGODLJIVOST",
        h2a: "Popolnoma samostojen. Zgrajen, da",
        h2em: "spoštuje tvoj svet.",
        learnMore: "Spoznaj integracije",
        cards: [
          {
            title: "Tvoje naloge, brez trenja.",
            body: "Journail vključuje lasten, zmogljiv in minimalističen sistem nalog, zgrajen za čisti fokus. Deluje vrhunsko že sam po sebi kot neodvisen delovni prostor.",
          },
          {
            title: "Poveži svoj ekosistem.",
            body: "Journail je AI dnevnik za vsakodnevno načrtovanje. Poveži Google Koledar z enim klikom: Journail prebere tvoje dogodke v koledarju, da sestavi tvoj dnevni načrt, ter doda ali posodobi dogodke, ki jih razporediš iz planerja. Todoist in e-mail beleženje delujeta enako — vsaka integracija je neobvezna in jo lahko kadarkoli prekineš.",
          },
          {
            title: "Tvoje misli so samo tvoje. Pika.",
            body: "Dnevnik je najbolj intimen prostor, ki ga imaš. Naša ekipa nima dostopa do tvojih zapisov (zero-knowledge). Podatki so šifrirani, varni in se nikoli ne uporabljajo za učenje javnih modelov umetne inteligence.",
          },
        ],
      },
      productPages: {
        ctaLine: "Začni svoj 7-dnevni preizkus.",
        dailyRhythm: {
          kicker: "// DNEVNI RITEM",
          h2: "Strateško načrtovanje dneva v štirih mirnih korakih.",
          lead: "Zjutraj načrtuj v smeri svojih ciljev, čez dan zaščiti fokusirano delo, zvečer zaključi s strukturirano dnevno refleksijo. Pet minut na dan ohranja tvoje delo usklajeno s tem, kar šteje — za ostalo poskrbi ritem.",
        },
        aiAssistant: {
          kicker: "// AI POMOČNIK",
          h2: "AI coach za produktivnost, ki pozna tvoje cilje.",
          lead: "Pozabi na skakanje med planerji, beležkami in generičnimi AI okni. Journailov pomočnik živi v tvojem delovnem prostoru kot AI upravljalec nalog in coach — pozna vsak cilj, ki ga zasleduješ, in tvoje nedavne vnose, načrtuje in ureja v naravnem jeziku ter vsak večer spremeni v AI-podprto refleksijo.",
        },
        integrations: {
          kicker: "// INTEGRACIJE",
          h2: "Tvoj sistem vse-v-enem. Opcijsko po zasnovi.",
          lead: "Journail je sistem vse-v-enem za delo in življenje, ki stoji sam. Ko želiš, poveži Google Koledar ali Todoist kot svoj AI upravitelj opravil in polni dnevnik kar po e-pošti — brez maratona nastavitev, brez ujetosti.",
        },
        goals: {
          kicker: "// CILJI",
          h2: "Aplikacija za načrtovanje ciljev, ki te vsak dan usklajuje.",
          lead: "Postavi letne, mesečne in tedenske cilje, nato poveži vsakodnevne naloge nanje. Avtomatizirano sledenje ciljem in sistem za usklajevanje ciljev poskrbita, da tvoj dan vleče k tistemu, kar šteje — ne le k temu, kar je nujno.",
        },
        journal: {
          kicker: "// DNEVNIK",
          h2: "Dnevnik produktivnosti, ki se piše sam.",
          lead: "Journail je AI dnevnik in aplikacija za refleksivni dnevnik v enem: digitalni planer z AI zjutraj, ki zvečer iz tvojega dejanskega dela sestavi vnos. Tvoj arhiv postane iskljiv spomin odločitev, vzorcev in napredka.",
        },
      },
      method: {
        kicker: "// METODA",
        headline: "En krog. Štiri faze.\nVsak dan boljša pot.",
        p1: "Večina aplikacij za produktivnost vam daje orodja. Journail vam daje ritem.",
        p2: "Za začetek AI pregleda vaš koledar, cilje in odprte naloge — ter vam pomaga izbrati tisto, kar je danes bistveno. Ne dvajset opravil. Dve ali tri.",
        p3: "Ko končate, vam isti AI pomaga pogledati nazaj. Pogovor postane vaš dnevniški zapis, ohranjen v vašem glasu in shranjen v vaši časovnici.",
        close: "Čez leto dni boste imeli nekaj, kar bo vredno prebrati.",
      },
      problem: {
        kicker: "// REALNOST",
        titleA: "Cena",
        titleEm: "razdrobljenih dni.",
        description:
          "Tvoje naloge živijo v enem orodju. Tvoj koledar v drugem. Tvoji dolgoročni cilji? Tiho čakajo na prost trenutek, ki ga nikoli ni. Dneve končuješ izčrpan, s kljukanjem neskončnih malenkosti, medtem ko se sprašuješ, ali si sploh naredil kakšen korak naprej pri stvareh, ki so resnično pomembne.",
        solution:
          "Journail spremeni način, kako je sestavljen tvoj dan. S tem ko tvojo širšo vizijo postavi neposredno v tvoj vsakdanji delovni prostor, poskrbi, da nujni hrup nikoli ne preglasi tvojih velikih ciljev.",
      },
      day: {
        kicker: "// DAN Z JOURNAILOM",
        headline: "Štirje trenutki. En tih krog.",
        tabs: [
          { time: "06 : 30", label: "Jutranji brief" },
          { time: "09 : 00", label: "Fokus" },
          { time: "18 : 30", label: "Večerni pregled" },
          { time: "20 : 00", label: "Zgodba dneva" },
        ],
        figLabel: "SL. 02 · JUTRANJI BRIEF",
        sidebar: {
          today: "Danes",
          archive: "Arhiv",
          items: ["Jutranji brief", "Fokus", "Večerni pregled", "Zgodba"],
          archiveItems: ["20. apr · ponedeljek", "19. apr · nedelja", "18. apr · sobota"],
        },
        mockDate: "Ponedeljek · 20. april · 06 : 34",
        mockHeading: "Dobro jutro. Tri stvari, o katerih danes razmišljaš.",
        mockAi1:
          "Ob 14:00 imaš pregled specifikacije in odprt pogovor s Sofio od petka. Tvoj cilj za Q2 je »izdati v1 za 50 uporabnikov«. Kaj je danes najpomembnejše?",
        mockYou: "Specifikacija za Sofio. Obljubil sem ji osnutek do poldneva.",
        mockAi2:
          "Razumem. Rezerviral sem 09:30–12:00 za osnutek in 30-minutno rezervo pred pregledom. Do poldneva utišam vse ostalo.",
        mockWho: { ai: "Journail", you: "Ti" },
        rightTitle: "Današnji plan",
        rightGoal: "Cilj v pogledu",
        tasks: [
          { label: "Osnutek specifikacije za Sofio", meta: "09 : 30 — 12 : 00 · Globoko delo" },
          { label: "Pregled specifikacije", meta: "14 : 00 — 15 : 00 · Koledar" },
          { label: "Odgovor Marku o ceniku", meta: "15 : 30 · 10 min" },
        ],
        goalLabel: "Izdaj v1 za 50 uporabnikov",
        goalMeta: "Q2 cilj · 3. teden od 12",
        more: "Poglej Fokus, Večerni pregled in Zgodbo dneva →",
      },
      compare: {
        kicker: "// PRIMERJAVA",
        headlineA: "Edini, ki sklene",
        headlineEm: "krog.",
        lead: "Aplikacije za produktivnost načrtujejo tvoj dan. Dnevniki ga beležijo. Journail je edini, ki počne oboje — z umetno inteligenco, ki si zapomni celoten kontekst.",
        featureHead: "Funkcija",
        journailHead: "Journail",
        competitors: ["Sunsama", "Motion", "Rosebud", "Day One"],
        rows: [
          { feature: "AI jutranje načrtovanje", values: [true, false, true, false, false] },
          { feature: "AI večerna refleksija", values: [true, false, false, true, false] },
          { feature: "Samodejni dnevnik", values: [true, false, false, false, false] },
          { feature: "Hierarhični cilji", values: [true, false, false, false, false] },
        ],
        note: "// Glede na javno dostopne podatke iz aprila 2026.",
        dragHint: "Povleci za primerjavo →",
        seeMore: "Preberi celotne primerjave",
        allComparisons: "Vse primerjave",
      },
      integrations: {
        kicker: "// INTEGRACIJE",
        headline: "Deluje s tem, kar že uporabljaš.",
        items: [
          {
            name: "Todoist",
            desc: "Opcijsko — Journail ima vgrajen lasten upravitelj nalog, zato ne potrebuješ ničesar drugega. Imaš raje Todoist? Poveži ga za dvosmerno sinhronizacijo: jutranji plan se zapiše kot naloge z roki, opravljene naloge pa se sinhronizirajo v obe smeri.",
          },
          {
            name: "Google Koledar",
            desc: "Dvosmerna sinhronizacija, ne le branje. Journail bere tvoje dogodke za načrt dneva ter ustvarja in posodablja dogodke, ki jih razporediš iz planerja. Deluje z več koledarji in računi.",
          },
          {
            name: "Email",
            desc: "Jutranji brief prispe v tvoj nabiralnik. Večerni pregled te tam čaka. Brez obvestil, ki kvarijo fokus.",
          },
        ],
        foot: "// Prihajajo še: Apple Reminders · Microsoft To Do · Notion.",
      },
      faq: {
        kicker: "// POGOSTA VPRAŠANJA",
        headlineA: "Vprašanja,",
        headlineEm: "preden začneš.",
        items: [
          {
            q: "Ali Journail uri AI modele na mojih zapisih?",
            a: "Ne. Tvoje besede so šifrirane v prenosu in mirovanju, uporabljene so samo za tvoje odgovore in nikoli ne trenirajo modelov — niti naših niti tujih.",
          },
          {
            q: "Kaj se zgodi, ko se 7-dnevna preizkusna doba konča?",
            a: "Nič samodejnega. Kreditne kartice ne zahtevamo vnaprej, zato te ne moremo presenetiti. Po 7 dneh ostaneš na brezplačni ravni, dokler se sam ne odločiš za nadgradnjo.",
          },
          {
            q: "V čem se razlikuje od Notiona ali navadnega dnevnika?",
            a: "Notion je prazno platno, dnevnik prazna stran. Journail je struktura in ritem — AI prebere tvoj kontekst (koledar, cilje, naloge) in ti pomaga načrtovati ter reflektirati.",
          },
          {
            q: "Ali lahko izvozim vse, kar zapišem?",
            a: "Kadar koli. Markdown za vse vnose, JSON za strukturirane podatke. Tvoje besede so tvoje — če greš, vzameš vse s seboj.",
          },
        ],
      },
      pricing: {
        kicker: "// CENIK",
        headlineA: "Cene v celih številkah.",
        headlineB: "Brez trikov.",
        toggleMonthly: "Mesečno",
        toggleYearly: "Letno",
        toggleSave: "Prihraniš ~23 %",
        perMonth: "/mesec",
        forever: "za vedno",
        noCard: "Brez kreditne kartice · Prekliči kadarkoli",
        billedYearly: "Obračunano letno",
        billedMonthly: "Obračunano mesečno",
        badgePopular: "Najbolj priljubljeno",
        vatIncluded: "DDV vključen",
        plans: [
          {
            eyebrow: "PRO",
            title: "Zapiraj krog vsak dan.",
            priceYearly: "€7",
            priceMonthly: "€9",
            subYearly: "€84 obračunano letno — prihraniš €24",
            subMonthly: "Obračunano mesečno",
            cta: "Začni 7-dnevni preizkus",
            features: [
              "Standardni AI dostop (Velikodušne dnevne limite, popolne za vsakodnevno rutino)",
              "Do 2 Google koledarjev",
              "Celoten arhiv dnevnika",
              "Hierarhični cilji (leto → mesec → teden)",
              "Todoist dvosmerna sinhronizacija",
              "Jutranji in večerni email",
              "Markdown in JSON izvoz",
              "Sledenje odsotnosti",
              "Svetli in temni način",
            ],
          },
          {
            eyebrow: "PREMIUM",
            title: "Za napredne uporabnike.",
            priceYearly: "€12",
            priceMonthly: "€15",
            subYearly: "€144 obračunano letno — prihraniš €36",
            subMonthly: "Obračunano mesečno",
            cta: "Začni 7-dnevni preizkus",
            features: [
              "Vse iz Pro, dodatno:",
              "Razširjen AI dostop (Znatno višje limite za poglobljene pogovore in intenzivno uporabo)",
              "Neomejeno Google koledarjev",
              "Napredni AI uvidi (vzorci, razpoloženje) [soon]",
              "Prednostna podpora",
              "Zgodnji dostop do novih funkcij",
            ],
          },
        ],
        promise: {
          kicker: "// NAŠA OBLJUBA GLEDE CEN",
          text: "Preizkusna doba se ne pretvori samodejno. Nikoli te ne bomo zaračunali za nekaj, kar si pozabil odpovedati.",
          coda: "Dlje ko uporabljaš Journail, več ti vrača — tvoj arhiv je tvoj, izvozljiv kadarkoli, vsak dan zapisa pa ga naredi vrednejšega.",
          sign: "— EKIPA JOURNAIL",
        },
        bridge: "Dnevni planer in dnevnik za eno ceno. Arhiv raste brezplačno, plačaš za Journail, ki ti ga bere nazaj.",
      },
      archive: {
        kicker: "// ARHIV",
        headlineA: "Leto tebe,",
        headlineEm: "v tvojih besedah",
        headlineB: ".",
        leadA: "Večina aplikacij tvojo preteklost zgolj arhivira. Journail jo uporabi.",
        leadB: "Vsak jutranji brief, vsak plan, vsak večerni pregled postane del arhiva, ki ti ga AI pomaga brati — ne kot nostalgijo, ampak kot povratno informacijo.",
        leadC: "Po šestih mesecih boš videl stvari, ki ti jih noben koledar ne pokaže. Vzorce kako začenjaš tedne. Cilje, ki si jih omenil, a nisi premaknil. Dni, ki si jih pozabil, pa so se izkazali za pomembne.",
        soft: "Kmalu ti bo arhiv začel govoriti nazaj. Vzorci, ki jih AI vidi, ti pa ne.",
        link: "Poglej načrt razvoja →",
        botDate: "sre · 22. apr · 2026",
        botPreview: "Počasen začetek, nato tih del fokusa na predlogu — dan, ki se ne zdi velik, dokler ga ne prebereš nazaj.",
        hint: "AI je ta teden zaznal vzorec.",
        midDate: "čet · 23. apr · 2026",
        midPreview: "Jutro sem zadržal za globoko delo in se obrestovalo — spec je končno kliknil. Domov sem šel po dolgi poti, da sem premislil, kaj sledi.",
        midMeta: "2 opravljena taska · povezano z 1 ciljem",
        topDate: "petek · 24. april · 2026",
        topClosed: "Zaključeno",
        topQuote: "Sklenil sem krog na partnerskem briefu. Iskreno o tem, kar je še mehko — v torek odpremimo tako ali tako.",
        topPreview: "Današnji dan je držal skupaj. Jutro je šlo v brief, ki je zdaj dovolj trden za pošiljanje. Popoldanski klic z ekipo je nejasno skrb spremenil v odločitev — raje zožimo obseg, kot prestavimo datum. Po negotovem tednu sem znova začutil veliki cilj.",
        topMeta: "3 opravljeni taski · 1 koledarski dogodek · povezano z 2 ciljema",
        entries: [
          {
            date: "ČET · 7. MAJ",
            quote: "Mayo Clinic je prešel iz pilota v plačljivo storitev — naš največji posel doslej.",
            preview: "Vodja kardiologije je rekel, da osebje sploh ne posega več po starih monitorjih. Reakcija ekipe je pokazala, kakšen zagon nam to daje.",
            meta: "8 min · povezano z 2 ciljema",
          },
          {
            date: "SRE · 6. MAJ",
            quote: "Prvi cel blok globokega razmišljanja v dveh tednih. Memo končno zveni iskreno.",
            preview: "Memo za upravni odbor sem napisal iz nič — prejšnji osnutki so zveneli preveč zglajeno. Ta pove resnico: odpremamo počasneje, kot sem upal, in stranke nas imajo bolj rade, kot sem pričakoval.",
            meta: "11 min · povezano z 3 cilji",
          },
          {
            date: "TOR · 5. MAJ",
            quote: "Odrekel sem hitremu pilotu, ki ga ne bi mogli dostojno servisirati. Integriteta se sešteva.",
            preview: "Cleveland Clinic je hotel šest enot v 30 dneh. Tega ne moremo, ne da bi obremenili podporno ekipo. Predlagal sem faznega začetka.",
            meta: "9 min · povezano z 1 ciljem",
          },
          {
            date: "PON · 4. MAJ",
            quote: "Sasha je opazila tveganje, ki sem ga podcenjeval. Zaupanje, ki si ga prislužiš s poslušanjem.",
            preview: "Rekla je, da FDA časovnica, ki sem jo potiskal, ni realna s trenutnim dokaznim paketom. Prav je imela. Prilagodil sem plan in prevzel odgovornost za klinično pripoved.",
            meta: "14 min · povezano z 2 ciljema",
          },
          {
            date: "PET · 1. MAJ",
            quote: "Pokritost cevovoda se je dvignila z 2,1× na 3,4×, ko smo počistili duplikate.",
            preview: "90-minutni pregled je premaknil mojo predstavo o poslu bolj kot zadnji trije pregledi napovedi skupaj. Ozko grlo so bila poročila, ne sam lijak.",
            meta: "7 min · povezano z 2 ciljema",
          },
          {
            date: "ČET · 30. APR",
            quote: "Strojna oprema v roki bere drugače kot na zaslonu.",
            preview: "Prva proizvodna serija je prispela. Zaobljenost na sponki je končno prava. Pritrditev baterije je še preveč tesna — oddal sem zahtevek za popravek.",
            meta: "5 min · povezano z 1 ciljem",
          },
        ],
      },
      founder: {
        kicker: "// BESEDA USTANOVITELJA",
        paragraphs: [
          "Journail sem zgradil, ker me je zmotilo, da sem dan upravljal prek petih aplikacij — in se kljub temu zvečer počutil izgubljenega.",
          "Notion je držal moje cilje. Todoist naloge. Koledar sestanke. Nekje vmes sem nehal pisati dnevnik — čeprav mi je manjkal — ker je bilo pisanje vprašanja »kaj se je danes zgodilo?« iz nič vsak večer kot domača naloga.",
          "Journail je tisto, kar sem si želel. En prostor, kjer mi AI pomaga načrtovati zjutraj, reflektirati zvečer in vsak dan tiho spremeni v nekaj, kar bom čez pol leta rad prebral.",
          "Upam, da bo tebi naredil isto.",
        ],
        signName: "— Tomaž P.",
        signRole: "Ustanovitelj, Journail",
      },
      socialProof: {
        kicker: "// SKUPNOST IN VIZIJA",
        h2a: "Ustvarjeno za tiste, ki",
        h2em: "razmišljajo in ustvarjajo.",
        quotes: [
          {
            text: "Journail je prvo orodje, ki od mene ni zahtevalo, da spremenim celoten način dela. Preprosto in umirjeno mi pomaga uskladiti to, kar delam danes, s tem, kje želim biti naslednje leto.",
            author: "Luka · razvijalec programske opreme",
          },
          {
            text: "Končno prostor, ki ceni zasebnost in mir. AI pomočnik deluje kot osebni strateg, ki me opominja na moj napredek, namesto da bi mi samo metal pod nos neopravljene naloge.",
            author: "Sarah · VP Product",
          },
        ],
        founderTitle: "Zakaj sem zgradil Journail",
        founderLetter: [
          "Tako kot mnogi med vami, sem tudi sam leta preživel ujet v zanki neskončnih nalog. Dneve sem končeval izčrpan, z odkljukanimi desetinami opravkov, a z občutkom, da moji dolgoročni cilji niso premaknili niti za milimeter. Moje misli so bile raztreščene med opombami, aplikacijami in mentalnim hrupom.",
          "Journail sem zgradil, ker sem potreboval ščit. Želel sem prostor, ki te ne sili, da narediš več, ampak ti pomaga narediti tisto, kar res šteje. Sistem, kjer je tvoje vsakodnevno delo v ravnovesju s tvojo vizijo in kjer se tvoj trud čez čas čudovito pretopi v modrost, ki se stopnjuje.",
          "Hvala, ker soustvarjaš ta ritem.",
        ],
        founderSignature: "— Tomaž, ustanovitelj Journaila",
      },
      finalCta: {
        kicker: "// NASLEDNJI KORAK",
        line1: "Ne menjaj planerja.",
        em: "Spremeni sebe.",
        description: "Tu ne gre za aplikacijo; gre za 5-minutno dnevno zavezo, da postaneš bolj usklajena različica sebe. Tvoja pot se začne, ko nehaš hiteti. Začni 7-dnevni brezplačni preizkus in občuti transformacijo.",
        cta: "Začni svojo pot",
        micro: "7-dnevni preizkus. Nato €7/mesec. Kreditna kartica ni potrebna.",
      },
      footerNav: {
        product: {
          heading: "Produkt",
          links: [
            { label: "Dnevni ritem", href: "/product/daily-rhythm" },
            { label: "AI pomočnik", href: "/product/ai-assistant" },
            { label: "Cilji", href: "/product/goals" },
            { label: "Dnevnik", href: "/product/journal" },
            { label: "Integracije", href: "/product/integrations" },
          ],
        },
        company: {
          heading: "Podjetje",
          links: [
            { label: "O nas", href: "/sl/o-nas" },
            { label: "Kontakt", href: "/contact" },
          ],
        },
        resources: {
          heading: "Vodniki",
          links: [
            { label: "Navodila", href: "/guides/tutorials" },
            { label: "Naša sled", href: "/guides/our-trail" },
            { label: "Zgodbe", href: "/blog" },
            { label: "Primerjave", href: "/compare" },
            { label: "Pogosta vprašanja", href: "/faq" },
          ],
        },
        legal: {
          heading: "Pravno",
          links: [
            { label: "Politika zasebnosti", href: "/privacy" },
            { label: "Pogoji uporabe", href: "/terms" },
            { label: "Pravila o piškotkih", href: "/cookies" },
            { label: "AI obvestilo", href: "/ai" },
            { label: "GDPR pravice", href: "/gdpr" },
          ],
        },
        folio: "// USKLAJENI KROG · ŠTEVILKA 01",
      },
    },
  },
  auth: {
    login: {
      title: "Prijava",
      subtitle: "Prijava v tvoj AI dnevnik",
      email: "Email",
      emailPlaceholder: "tvoj@email.com",
      password: "Geslo",
      submit: "Prijava",
      noAccount: "Nimaš računa?",
      register: "Registracija",
      error: "Napačen email ali geslo",
      pendingError: "Tvoj račun še ni odobren. Počakaj na odobritev administratorja.",
      genericError: "Napaka pri prijavi",
      googleSignIn: "Prijava z Google",
      forgotPassword: "Pozabljeno geslo?",
    },
    register: {
      title: "Registracija",
      subtitle: "Ustvari nov račun za Journ",
      subtitleHighlight: "ai",
      subtitleEnd: "l",
      name: "Ime",
      namePlaceholder: "Tvoje ime",
      email: "Email",
      emailPlaceholder: "tvoj@email.com",
      password: "Geslo",
      passwordPlaceholder: "Vsaj 8 znakov",
      submit: "Registracija",
      hasAccount: "Že imaš račun?",
      login: "Prijava",
      error: "Napaka pri registraciji",
      existsError: "Uporabnik s tem emailom že obstaja",
      pendingTitle: "Registracija uspešna!",
      pendingMessage: "Tvoj račun čaka na odobritev administratorja. Obveščeni te bomo po emailu, ko bo račun aktiviran.",
      pendingBack: "Nazaj na prijavo",
    },
    forgot: {
      title: "Pozabljeno geslo",
      subtitle: "Vpiši svoj email in poslali ti bomo povezavo za ponastavitev gesla.",
      email: "Email",
      emailPlaceholder: "tvoj@email.com",
      submit: "Pošlji povezavo",
      sending: "Pošiljam…",
      backToLogin: "Nazaj na prijavo",
      sentTitle: "Preveri svoj nabiralnik",
      sentMessage: "Če za ta email obstaja račun, smo poslali povezavo za ponastavitev gesla. Povezava velja 1 uro.",
      genericError: "Nekaj je šlo narobe. Poskusi znova.",
    },
    reset: {
      title: "Novo geslo",
      subtitle: "Vnesi novo geslo za svoj račun.",
      password: "Novo geslo",
      passwordPlaceholder: "Vsaj 8 znakov",
      confirm: "Ponovi geslo",
      confirmPlaceholder: "Ponovi novo geslo",
      submit: "Shrani geslo",
      saving: "Shranjujem…",
      successTitle: "Geslo posodobljeno",
      successMessage: "Tvoje geslo je spremenjeno. Zdaj se lahko prijaviš z novim geslom.",
      goToLogin: "Pojdi na prijavo",
      mismatch: "Gesli se ne ujemata.",
      invalidToken: "Povezava je neveljavna ali je potekla. Zahtevaj novo.",
      requestNew: "Zahtevaj novo povezavo",
      genericError: "Nekaj je šlo narobe. Poskusi znova.",
    },
  },
  nav: {
    journal: "Danes",
    archive: "Dnevnik",
    goals: "Cilji",
    tasks: "Naloge in koledar",
    calendar: "Koledar",
    faq: "FAQ",
    guides: "Vodniki",
    privacy: "Zasebnost",
    terms: "Pogoji",
    settings: "Nastavitve",
    admin: "Admin",
    users: "Uporabniki",
    support: "Podpora",
    activity: "Aktivnost",
    logout: "Odjava",
    openMenu: "Odpri meni",
  },
  admin: {
    title: "Administracija",
    subtitle: "Pregled sistema in statistika",
    stats: {
      users: "Uporabniki",
      entries: "Vnosi",
      goals: "Cilji",
      pending: "Čakajoči",
    },
    links: {
      users: "Upravljanje uporabnikov",
      support: "Sporočila podpore",
      logs: "Dnevnik aktivnosti",
    },
    registrations: "Registracije (30 dni)",
    entriesChart: "Vnosi (30 dni)",
    date: "Datum",
    count: "Število",
    users: {
      title: "Uporabniki",
      subtitle: "Upravljanje uporabnikov in vlog",
      name: "Ime",
      email: "Email",
      role: "Vloga",
      registered: "Registriran",
      roleAdmin: "Admin",
      roleUser: "Uporabnik",
      rolePending: "V čakanju",
      approve: "Odobri",
      roleChanged: "Vloga spremenjena.",
      roleChangeError: "Napaka pri spremembi vloge.",
      deleteUser: "Izbriši uporabnika",
      deleteConfirm: "Ali res želiš izbrisati tega uporabnika? Vsi njegovi podatki bodo trajno izbrisani.",
      deleteSuccess: "Uporabnik izbrisan.",
      deleteError: "Napaka pri brisanju uporabnika.",
      deleteSelf: "Ne moreš izbrisati svojega računa.",
      language: "Jezik",
      lastActivity: "Zadnja aktivnost",
      expandProfile: "Razširi profil",
    },
    logs: {
      title: "Dnevnik aktivnosti",
      subtitle: "Pregled vseh akcij v sistemu",
      today: "Danes",
      days7: "7 dni",
      days30: "30 dni",
      all: "Vse",
      time: "Čas",
      type: "Tip",
      message: "Sporočilo",
      user: "Uporabnik",
      noLogs: "Ni zapisov",
      types: {
        user_registered: "Registracija",
        user_approved: "Odobritev",
        user_deleted: "Izbris uporabnika",
        user_login: "Prijava",
        role_changed: "Sprememba vloge",
        support_ticket: "Sporočilo",
        journal_created: "Nov vnos",
        goal_created: "Nov cilj",
      },
      filterByType: "Filtriraj po tipu",
      allTypes: "Vsi tipi",
    },
  },
  journal: {
    title: "Dnevnik",
    greeting: "Dobro jutro",
    todayPlan: "Današnji plan",
    startPlanning: "Začni načrtovanje",
    continuePlanning: "Nadaljuj načrtovanje",
    editPlan: "Uredi plan",
    finishDay: "Zaključi dan",
    noPlan: "Danes še nimaš načrta.",
    startNew: "Začni nov dan",
    planAhead: "Načrtuj vnaprej",
    unfinalizedEntries: "Nedokončani vnosi",
    unfinalizedWarning: "Nedokončani vnosi iz preteklih dni",
    markAsDone: "Označi kot opravljeno",
    viewEntry: "Poglej vnos",
    setupTitle: "Pred začetkom nastavi integracije",
    setupDescription: "Za polno izkušnjo poveži Todoist",
    setupDescriptionCalendar: " in Google Koledar",
    setupDescriptionEnd: " v nastavitvah.",
    settingsButton: "Nastavitve",
    unfinalizedTitle: "Imaš nezaključen dnevnik",
    unfinalizedNotFinished: "še ni zaključen.",
    unfinalizedHint: "Zaključi prejšnji dan preden začneš novega.",
    finishPrevious: "Zaključi prejšnji dan",
    entryExists: "Današnji vnos že obstaja",
    finalized: "Zaključen",
    finishJournal: "Zaključi dnevnik",
    openInArchive: "Odpri v arhivu",
    planAheadDesc: "Pripravi plan za prihodnje dni že danes.",
    tomorrow: "Jutri",
    dayAfter: "Pojutrišnjem",
    calendarEvents: "dogodkov v koledarju",
    tasksForDay: "taskov za ta dan",
    entryExistsWarning: "Vnos za ta dan že obstaja",
    editExistingPlan: "Uredi obstoječ plan",
    loadingData: "Nalagam podatke...",
    dataError: "Napaka pri nalaganju podatkov",
    conversationError: "Napaka pri začetku pogovora",
    todoistSkipped: "Todoist faza preskočena.",
    tasksUpdated: "taskov posodobljenih",
    tasksUpdateFailed: "neuspešnih",
    tasksUpdateError: "Napaka pri posodabljanju taskov",
    entrySaved: "Dnevniški vnos shranjen!",
    entrySaveError: "Napaka pri shranjevanju vnosa",
    dayFinished: "Dan zaključen! Email poslan. Osvežujem stran...",
    dayFinishError: "Napaka pri zaključevanju dneva",
    refreshError: "Napaka pri osveževanju taskov",
    startButton: "Začni z današnjim dnevnikom",
    startButtonLoading: "Zaganjam...",
    calendar: "Koledar",
    noEvents: "Ni dogodkov za danes",
    allDay: "(celodnevno)",
    tasks: "Taski",
    addTask: "Dodaj task",
    taskPlaceholder: "Ime taska...",
    timePlaceholder: "Ura",
    project: "Projekt",
    noProject: "Brez projekta",
    priorityUrgent: "Urgentno",
    priorityHigh: "Visoka",
    priorityMedium: "Srednja",
    priorityNormal: "Normalna",
    create: "Ustvari",
    priorityTasksSingular: "prioritetna naloga",
    priorityTasksPlural: "prioritetne naloge",
    priorityTasksMany: "prioritetnih nalog",
    andMore: "...in še",
    noTasks: "Ni taskov",
    overdue: "Zamujeni",
    forToday: "Za danes",
    completedToday: "Danes opravljeno",
    upcoming: "Prihajajoči",
    activeGoals: "Aktivni cilji",
    goalYearly: "Letni",
    goalMonthly: "Mesečni",
    goalWeekly: "Tedenski",
    tasksRefreshed: "Taski osveženi",
    tasksRefreshError: "Napaka pri osvežitvi taskov",
    taskCompleted: "Task označen kot opravljen",
    taskReopened: "Task ponovno odprt",
    taskUpdateError: "Napaka pri posodabljanju taska",
    taskDeleted: "Task izbrisan",
    taskDeleteError: "Napaka pri brisanju taska",
    taskContentRequired: "Vnesi vsebino taska",
    taskCreated: "Task ustvarjen",
    taskCreateError: "Napaka pri ustvarjanju taska",
    deleteTask: "Izbriši task",
    phases: {
      priorities: "Prioritete",
      taskSelection: "Pregled in predlog",
      myPlan: "Moj plan",
      completed: "Končano",
      taskReview: "Pregled taskov",
      comment: "Komentar",
      journal: "Dnevnik",
      editPlan: "Uredi plan",
      todoist: "Todoist",
    },
    continueToPhase: "Nadaljuj na ",
    jumpTo: "Skoči na:",
    inputPlaceholder: "Zapiši misli ali skoči naprej …",
    planRefinePlaceholder: "Potrdi plan z ↵, uredi s klikom na gumb ali zapiši komentar Journailu …",
    conversationDone: "Pogovor končan. Dnevniški vnos je shranjen v arhivu.",
    finalizingDay: "Zaključujem...",
    finalizeDay: "Zaključi dan",
    openInArchiveBtn: "Odpri v arhivu",
    editText: "Uredi besedilo",
    cancelEdit: "Prekliči",
    submitEdits: "Shrani",
    confirmAndSave: "Potrdi in shrani",
    saving: "Shranjujem...",
    confirmAndContinue: "Potrdi in nadaljuj",
    confirming: "Potrjujem...",
    saveAndFinish: "Shrani in zaključi",
    saveEntry: "Shrani vnos",
    editBtn: "Uredi",
    todayEntry: "Današnji vnos",
    taskReviewTitle: "Pregled taskov dneva",
    taskReviewSubtitle: "Označi opravljene taske in dodaj opombe",
    refresh: "Osveži",
    noTasksForToday: "Ni taskov za danes.",
    optionalNote: "Opcijska opomba...",
    notePlaceholder: "Opomba (npr. kaj še manjka)...",
    continueToReview: "Nadaljuj k pregledu",
    whichTasksToday: "Katere taske boš danes naredil?",
    overdueLabel: "ZAMUJENI",
    todayLabel: "ZA DANES",
    noTasksToSelect: "Ni taskov za izbiro.",
    taskContentPlaceholder: "Vsebina taska...",
    projectLabel: "Projekt",
    dueDateToday: "Danes",
    dueDateTomorrow: "Jutri",
    dueDateWeekend: "Vikend",
    dueDateNextWeek: "Nasl. teden",
    addBtn: "Dodaj",
    highlightsTitle: "Kaj je bilo danes pomembno?",
    highlightsSubtitle: "Zapiši ključne stvari dneva, ki jih želiš izpostaviti na timelinu (opcijsko)",
    highlightsPlaceholder: "Npr. Uspešno zaključil projekt X, dobil pomembno novico...",
    continueToMyPlan: "Nadaljuj na Moj plan",
    phaseCompleted: "✓ Končano",
    aiError: "Napaka pri komunikaciji z AI. Poskusi znova.",
    taskReviewError: "Napaka pri oddaji pregleda taskov",
    tasksCompleted: "taskov zaključenih!",
    cancelDelete: "Prekliči brisanje",
    deleteBtn: "Zbriši",
    rescheduleKeep: "Pusti kot je",
    rescheduleTomorrow: "Prestavi na jutri",
    rescheduleWeekend: "Prestavi na vikend",
    rescheduleNextWeek: "Prestavi na začetek naslednjega tedna",
    selectAll: "izberi vse",
    deselectAll: "odstrani vse",
    suggestionsFromGoals: "PREDLOGI IZ CILJEV",
    addNewTask: "DODAJ NOV TASK",
  },
  archive: {
    title: "Arhiv",
    subtitle: "Pretekli dnevniški vnosi",
    export: "Izvozi",
    noEntries: "Še nimaš vnosov",
    finalized: "Dokončan",
    draft: "Osnutek",
    loadMore: "Naloži več",
    noEntriesMessage: "Še ni dnevniških vnosov.",
    startJournalHint: "Začni z dnevnim pregledom na strani Dnevnik.",
    searchPlaceholder: "Išči po dnevniških zapisih…",
    searchNoResults: "Ni zadetkov za to iskanje.",
    searchResultsCount: "zadetkov",
  },
  goals: {
    title: "Cilji",
    subtitle: "Upravljanje ciljev",
    weekly: "Tedenski",
    monthly: "Mesečni",
    yearly: "Letni",
    addGoal: "Dodaj cilj",
    noGoals: "Ni ciljev",
    status: {
      active: "Aktiven",
      completed: "Dosežen",
      partial: "Delno dosežen",
      failed: "Nedosežen",
    },
    yearlyGoals: "Letni cilji",
    monthlyGoals: "Mesečni cilji",
    weeklyGoals: "Tedenski cilji",
    add: "Dodaj",
    noGoalsEmpty: "Ni ciljev. Dodaj prvega!",
    noActiveGoals: "Ni aktivnih ciljev.",
    showArchived: "Prikaži zaključene cilje",
    hideArchived: "Skrij zaključene cilje",
    archivedGoals: "zaključene cilje",
  },
  settings: {
    title: "Nastavitve",
    profile: {
      sectionTitle: "Moj profil",
      title: "Profil",
      description: "Tvoje ime, kot se prikaže v aplikaciji in emailih.",
      nameLabel: "Ime",
      namePlaceholder: "Tvoje ime",
      emailLabel: "Email",
      emailHint: "Email je vezan na tvoj račun in ga tukaj ni mogoče spremeniti.",
      avatarHint: "Profilna slika je prevzeta iz tvojega Google računa.",
      save: "Shrani",
      saved: "Profil shranjen.",
      errorSave: "Shranjevanje ni uspelo.",
      loading: "Nalagam …",
    },
    integrations: "Integracije",
    email: "Email obvestila",
    absences: "Odsotnosti",
    language: "Jezik",
    languageDescription: "Izberi jezik vmesnika",
    uiLanguage: "Jezik vmesnika",
    uiLanguageDescription: "Vpliva na prevode, gumbe, menije in emaile.",
    contentLanguage: "Jezik vsebine",
    contentLanguageDescription: "V tem jeziku se pogovarjaš z AI in v njem te razume — dnevnik, cilji, naloge. Lahko se razlikuje od jezika vmesnika.",
    contentLanguagePlaceholder: "Izberi jezik",
    taskProvider: {
      title: "Upravljanje nalog",
      description: "Izberi sistem za upravljanje nalog",
      internal: "Interni",
      internalDescription: "Vgrajen upravljalnik nalog s podporo za ponavljajoče naloge",
      todoistLabel: "Todoist",
      todoistDescription: "Sinhroniziraj naloge s Todoist računom",
      googleTasks: "Google Tasks",
      googleTasksDescription: "Sinhroniziraj naloge z Google Tasks",
      switchWarning: "Preklop ne izbriše podatkov v prejšnjem sistemu.",
      saved: "Upravljalnik nalog spremenjen",
      saveError: "Napaka pri spremembi upravljalnika nalog",
      projects: "Projekti",
      addProject: "Dodaj projekt",
      projectName: "Ime projekta",
      noProjects: "Ni projektov. Dodaj prvega.",
      projectAdded: "Projekt dodan",
      projectDeleted: "Projekt izbrisan",
      notImplemented: "Google Tasks integracija še ni na voljo.",
    },
    todoist: {
      description: "Poveži Todoist za uvoz in upravljanje taskov",
      connected: "Povezan",
      disconnected: "Nepovezan",
      connectedMessage: "Todoist je povezan. Taski se samodejno uvažajo v dnevnik.",
      disconnect: "Odklopi",
      apiKey: "API ključ",
      apiKeyPlaceholder: "Vnesi Todoist API ključ",
      apiKeyHelp: "Najdeš ga v",
      connect: "Poveži",
      connectSuccess: "Todoist uspešno povezan!",
      connectError: "Napaka pri povezovanju",
      disconnectSuccess: "Todoist odklopljen",
      disconnectError: "Napaka pri odklopu",
    },
    calendar: {
      description: "Poveži Google Calendar račune za prikaz dogodkov",
      disconnected: "Nepovezan",
      addAccount: "Dodaj Google račun",
      connectCalendar: "Poveži Google Calendar",
      loadingAccounts: "Nalagam račune...",
      selectCalendars: "Izberi koledarje:",
      saveSelection: "Shrani izbiro",
      noCalendarSelected: "Noben koledar ni izbran - prikazani bodo vsi.",
      calendarsCount: "koledarjev",
      connectError: "Napaka pri povezovanju Google Calendar",
      accountRemoved: "Google račun odstranjen",
      removeError: "Napaka pri odstranjevanju računa",
      calendarsSaved: "Izbrani koledarji shranjeni",
      saveError: "Napaka pri shranjevanju",
      verificationTitle: "Povezava s koledarjem je v postopku Googlove verifikacije",
      verificationBody: "Journail je pri Googlu v postopku odobritve dostopa do koledarja. Do zaključka boš ob povezovanju videl Googlov zaslon »Google ni preveril te aplikacije«. To je pričakovano in varno — gre za našo aplikacijo. Za nadaljevanje klikni »Dodatno« in nato »Pojdi na Journail«. Tvoji podatki ostanejo zasebni; uporabljamo jih le za prikaz tvojega dneva.",
    },
    emailNotifications: {
      title: "Email obvestila",
      description: "Prejmi opomnik za jutranji ali večerni dnevnik",
      emailAddress: "Email naslov",
      morningEmail: "Jutranji email",
      morningDescription: "AI generiran plan za dan na podlagi tvojih ciljev, koledarja in taskov.",
      sendAt: "Pošiljanje ob",
      eveningReminder: "Večerni opomnik",
      eveningDescription: "Opomnik za zaključek dneva z linkom. Če ni vnosa, pošlje taske za danes.",
      receiveWeekends: "Prejemaj tudi ob vikendih",
      saved: "Nastavitve shranjene",
      saveError: "Napaka pri shranjevanju",
    },
    workHours: {
      sectionTitle: "Tvoj delovni čas",
      title: "Delovni čas",
      description: "Nastavi začetno in končno uro svojega delovnega dneva. Po tem se prilagodi usklajen krog, statusi v sredinski koloni, ter ura, ob kateri prejmeš jutranji in večerni email.",
      startLabel: "Začetek (jutranji email)",
      endLabel: "Konec (večerni email)",
      timezoneLabel: "Časovna cona",
      timezoneHint: "Cona iz tvojega brskalnika",
      detectTz: "Zaznaj",
      save: "Shrani",
      saved: "Delovni čas posodobljen",
      loading: "Nalagam …",
      errorRange: "Končna ura mora biti večja od začetne.",
      errorSave: "Napaka pri shranjevanju.",
    },
    absence: {
      title: "Dopust / odsotnost",
      description: "V času odsotnosti ne boš prejel/a email obvestil",
      from: "Od",
      to: "Do",
      reason: "Razlog (opcijsko)",
      reasonPlaceholder: "npr. Dopust, Bolniška",
      add: "Dodaj odsotnost",
      added: "Odsotnost dodana",
      removed: "Odsotnost odstranjena",
      dateError: "Začetni datum mora biti pred končnim",
      saveError: "Napaka pri shranjevanju",
      deleteError: "Napaka pri brisanju",
    },
    billing: {
      title: "Naročnina",
      freePlan: "Brezplačno",
      proPlan: "PRO",
      premiumPlan: "PREMIUM",
      statusActive: "Aktivno",
      statusPastDue: "Plačilo zamuja",
      statusCancelled: "Preklicano",
      statusExpired: "Poteklo",
      statusTrial: "Preizkusno",
      activeUntil: "Aktivno do",
      cancelsOn: "Preklic stopi v veljavo",
      pastDueNote: "Zadnje plačilo ni uspelo. Posodobi kartico, da ohraniš naročnino.",
      manage: "Upravljaj naročnino",
      invoices: "Računi",
      cancelSub: "Prekliči naročnino",
      cancelConfirm: "Te bo odpeljalo na portal LemonSqueezy, kjer lahko prekličeš naročnino. Po preklicu ostaneš na trenutnem planu do konca obračunskega obdobja.",
      cancelTrial: "Prekliči preizkus",
      cancelTrialConfirm: "Si prepričan, da želiš prekiniti brezplačni preizkus? Tvoj plan se bo takoj prevedel na brezplačno raven in izgubil boš dostop do plačljivih AI funkcionalnosti.",
      cancelTrialDone: "Preizkus preklican. Plan je nastavljen na brezplačno.",
      noSubHint: "Tvoj plan je dodeljen ročno (brez prave naročnine). Za račune in upravljanje opravi nakup zgoraj.",
      changePlan: "Zamenjaj plan",
      upgradeCta: "Nadgradi",
      switchCta: "Preklopi",
      currentPlanBadge: "Tvoj paket",
      vatIncluded: "DDV vključen",
      paymentFailedHelp: "Posodobi plačilni način",
      loadingStatus: "Nalagam stanje naročnine…",
      successToast: "Naročnina uspešno aktivirana.",
      cycleMonthly: "mesečno",
      cycleYearly: "letno",
    },
    changePassword: {
      sectionTitle: "Geslo",
      title: "Spremeni geslo",
      description: "Posodobi geslo za prijavo v svoj račun.",
      current: "Trenutno geslo",
      currentPlaceholder: "Trenutno geslo",
      new: "Novo geslo",
      newPlaceholder: "Vsaj 8 znakov",
      confirm: "Ponovi novo geslo",
      confirmPlaceholder: "Ponovi novo geslo",
      submit: "Spremeni geslo",
      saving: "Shranjujem…",
      saved: "Geslo posodobljeno.",
      errorCurrent: "Trenutno geslo ni pravilno.",
      errorMismatch: "Novi gesli se ne ujemata.",
      errorShort: "Geslo mora imeti vsaj 8 znakov.",
      errorGeneric: "Sprememba ni uspela. Poskusi znova.",
      noPasswordHint: "Prijavljaš se z Googlom in še nimaš nastavljenega gesla. Uporabi “Pozabljeno geslo?” na prijavni strani, da ga nastaviš.",
    },
    dataPrivacy: {
      sectionTitle: "Podatki in zasebnost",
      exportTitle: "Izvozi svoje podatke",
      exportDescription: "Prenesi vse svoje podatke — profil, dnevnike, cilje, opravila, pogovore in nastavitve — v enem JSON paketu.",
      exportButton: "Prenesi vse (JSON)",
      exporting: "Pripravljam…",
      exportError: "Izvoz ni uspel. Poskusi znova.",
    },
    deleteAccount: {
      sectionTitle: "Nevarno območje",
      title: "Izbris računa",
      description: "Trajno izbriše tvoj račun in vse podatke — dnevnike, cilje, opravila, pogovore in nastavitve.",
      warning: "Tega dejanja ni mogoče razveljaviti.",
      exportHint: "Pred izbrisom lahko izvoziš svoje podatke v razdelku zgoraj.",
      button: "Izbriši račun",
      confirmPhrase: "IZBRIŠI",
      confirmPrompt: "Za potrditev vtipkaj IZBRIŠI",
      passwordLabel: "Potrdi z geslom",
      passwordPlaceholder: "Tvoje geslo",
      confirmButton: "Trajno izbriši račun",
      cancel: "Prekliči",
      activeSubError: "Najprej prekliči naročnino v razdelku Naročnina, nato izbriši račun.",
      invalidPassword: "Napačno geslo.",
      genericError: "Izbris ni uspel. Poskusi znova.",
      deleting: "Brišem…",
    },
  },
  support: {
    title: "Pomoč & predlogi",
    newTicket: "Novo sporočilo",
    myMessages: "Moja sporočila",
    subject: "Zadeva",
    subjectPlaceholder: "Kratek opis težave ali predloga",
    message: "Sporočilo",
    messagePlaceholder: "Opiši podrobnosti...",
    send: "Pošlji",
    sent: "Sporočilo poslano!",
    sendError: "Napaka pri pošiljanju.",
    replyError: "Napaka pri pošiljanju odgovora.",
    noMessages: "Ni sporočil",
    createNew: "Ustvari novo zadevo za pomoč.",
    open: "Odprto",
    resolved: "Rešeno",
    markResolved: "Označi kot rešeno",
    reopen: "Ponovno odpri",
    statusChanged: "Status spremenjen.",
    statusError: "Napaka pri spremembi statusa.",
    writeReply: "Napiši odgovor...",
    floatingButton: "Imate težavo ali predloge za spremembo? Pišite nam!",
    user: "Uporabnik",
    adminLabel: "Admin",
  },
  notifications: {
    title: "Obvestila",
    markAllRead: "Označi vse kot prebrano",
    noNotifications: "Ni obvestil",
  },
  emails: {
    journalEntry: "Dnevniški vnos",
    morningPlan: "Današnji plan",
    eveningReminder: "Večerni opomnik",
    openJournal: "Odpri dnevnik",
    finishJournal: "Zaključi dnevnik",
    startJournal: "Začni dnevnik",
    completedTasks: "Opravljeni taski",
    overdue: "Zamujeni",
    today: "Danes",
    tasksForToday: "Taski za danes",
    noOpenTasks: "Danes nimaš odprtih taskov.",
    autoGenerated: "Avtomatsko generirano s Journail",
    important: "Pomembno",
    entryExists: "Danes si že ustvaril dnevniški vnos. Čas je za zaključek dneva!",
    noEntry: "Danes še nimaš dnevniškega vnosa. Tukaj so tvoji taski za danes:",
    newUserRegistered: "Nov uporabnik registriran",
    accountApproved: "Tvoj račun je odobren!",
    accountApprovedMessage: "Tvoj račun na Journail je bil odobren. Zdaj se lahko prijaviš in začneš z uporabo aplikacije.",
    loginNow: "Prijava",
    signoffMorning: "Lep dan,\n— Journail",
    signoffEvening: "Lahko noč,\n— Journail",
    signoffJournal: "Misli so na papirju, glava je prosta. Lepo preživi današnji dan.\n— Journail",
    plan: "Plan",
    summary: "Povzetek",
    eyebrowMorning: "Jutranji brief",
    eyebrowEvening: "Večerni opomnik",
    eyebrowJournal: "Dnevniški vnos",
    eyebrowApproval: "Tvoj račun",
    eyebrowAdmin: "Admin",
    eyebrowReset: "Ponastavitev gesla",
    passwordResetTitle: "Ponastavi geslo",
    passwordResetMessage: "Prejeli smo zahtevo za ponastavitev gesla tvojega Journail računa. Klikni spodnji gumb in nastavi novo geslo.",
    passwordResetExpiry: "Povezava velja 1 uro.",
    passwordResetIgnore: "Če nisi zahteval ponastavitve, lahko ta email mirno prezreš — tvoje geslo ostane nespremenjeno.",
    resetCta: "Ponastavi geslo",
    adminPendingHeading: "Nov uporabnik čaka na odobritev",
    adminPendingNameLabel: "Ime",
    adminPendingDateLabel: "Datum",
    adminOpenPanel: "Odpri admin panel",
    eyebrowTrial: "Tvoj preizkus",
    trialStartedTitle: "Tvoj 7-dnevni preizkus se je začel.",
    trialStartedMessage: "Dobrodošel v Journailu. Naslednjih 7 dni imaš poln dostop do načrta Pro — AI pomočnik, planiranje in dnevnik. Kreditna kartica ni potrebna. Začni s prvim krogom svojega dne.",
    trialOpenCta: "Odpri Journail",
    trialReminderTitle: "Tvoj preizkus se kmalu izteče.",
    trialReminderDays: "Do konca preizkusa: {n}.",
    trialReminderMessage: "Da obdržiš AI pomočnika, planiranje in dnevnik tudi po preizkusu, izberi naročnino. Brez prekinitve — vse tvoje vsebine ostanejo na svojem mestu.",
    trialReminderCta: "Izberi naročnino",
    trialEndedTitle: "Tvoj preizkus je potekel.",
    trialEndedMessage: "Tvoj 7-dnevni preizkus se je iztekel. Tvoje vsebine so varno shranjene. Za ponovno aktivacijo AI pomočnika in planiranja sklenite naročnino — vse se nadaljuje točno tam, kjer si ostal.",
    trialEndedCta: "Aktiviraj naročnino",
    adminSignupHeading: "Nova prijava na preizkus",
    footerNote: "Avtomatsko poslano. Lahko mi odgovoriš direktno.",
    unsubscribeNote: "Pošiljanje e-pošte lahko izklopiš v nastavitvah.",
    settingsLink: "Nastavitve e-pošte",
    settingsLinkLabel: "Spremeni v nastavitvah",
  },
  consent: {
    banner: {
      title: "Beležka o piškotkih",
      description: "Nekaj piškotkov je nujnih, da ostaneš prijavljen in da storitev deluje. Z dovoljenjem bi radi uporabili tudi analitiko (Google Analytics, oglaševalske funkcije izklopljene), da vidimo, kaj izboljšati. Brez oglasov. Vaših dnevniških vnosov nikoli ne pošiljamo v analitiko.",
      acceptAll: "Sprejmem vse",
      rejectAll: "Zavrnem vse",
      customize: "Nastavi po meri",
      saveSelection: "Shrani izbiro",
      noticeTitle: "Beležka o piškotkih",
      noticeDescription: "Nastavimo nekaj piškotkov, ki so nujni za prijavo. Tržnih sledilcev ne uporabljamo. Analitiko lahko upraviš spodaj ali z gumbom \"Do Not Sell or Share My Personal Information\" v nogi.",
      doNotSell: "Do Not Sell or Share My Personal Information",
    },
    categories: {
      necessary: {
        title: "Nujno potrebni",
        description: "Potrebni za delovanje storitve. Ohranjajo prijavo, varujejo pred napadi, pomnijo tvoj izbrani jezik. Ni jih mogoče izklopiti.",
      },
      functional: {
        title: "Funkcionalni",
        description: "Pomnijo izbire, kot je razporeditev nadzorne plošče. Izboljšajo udobnost, a storitev deluje tudi brez njih.",
      },
      analytics: {
        title: "Analitika",
        description: "Google Analytics in PostHog (EU), da vidimo, kaj izboljšati. Oglaševalske funkcije so izklopljene, IP naslovi se ne shranjujejo, dnevniških vnosov, ciljev in AI pogovorov pa nikoli ne pošiljamo v analitiko.",
      },
      marketing: {
        title: "Trženje",
        description: "Tržnih piškotkov ne uporabljamo, na journail.app ni oglasnih sledilcev tretjih oseb.",
      },
    },
    settings: {
      title: "Piškotki in zasebnost",
      subtitle: "Upravljaj svoja soglasja za piškotke in tržne tehnologije.",
      manage: "Upravljaj nastavitve",
      currentChoices: "Trenutna izbira",
      lastUpdated: "Zadnja sprememba",
      privacyLink: "Preberi pravilnik o zasebnosti",
    },
    admin: {
      title: "Soglasja",
      subtitle: "Pregled zapisov o soglasjih (proof-of-consent).",
      version: "Trenutna različica pravilnika",
      filterRegion: "Regija",
      filterAll: "Vse",
      time: "Čas",
      subject: "Subjekt",
      user: "Uporabnik",
      region: "Regija",
      country: "Država",
      event: "Dogodek",
      categories: "Kategorije",
      gpc: "GPC",
      empty: "Še ni zapisov.",
    },
    footer: {
      privacy: "Zasebnost",
      terms: "Pogoji",
      cookies: "Piškotki",
      preferences: "Nastavitve piškotkov",
    },
  },
  composer: {
    modeSwitch: {
      eyebrow: "// SPREMEMBA NAČINA",
      titleTo: "Zamenjati v način {toLabel}?",
      bodyFromTo: "AI bo prešel iz načina {fromLabel} v način {toLabel}. Tvoj kontekst ostane.",
      cancelKeep: "Obdrži {fromLabel}",
      confirm: "Zamenjaj",
      modePlan: "Plan",
      modeReflect: "Reflect",
      modeAsk: "Ask",
    },
    slash: {
      taskAdd: "Dodaj nalogo k današnjemu seznamu",
      taskEdit: "Uredi nalogo (poišči → izberi)",
      taskComplete: "Zaključi nalogo (poišči → izberi)",
      taskDelete: "Izbriši nalogo (poišči → izberi)",
      taskMove: "Prestavi nalogo (poišči → izberi datum)",
      taskCompleteSoon: "Izbirnik kmalu na voljo",
      journalSearch: "Išči po dnevnikih",
      note: "Dodaj beležko (AI ne odgovori; upošteva se ob zaključku dneva)",
      noteAdded: "Beležka dodana",
      noteUnavailable: "Beležke so na voljo med dnevom — po sprejetem planu.",
      today: "Skoči na današnji dan",
      yesterday: "Odpri včerajšnji dnevnik",
      yesterdayMissing: "Včerajšnjega dnevnika še ni.",
      help: "Pokaži vse ukaze",
      helpToast: "Ukazi: /task /journal /today /yesterday /tomorrow /help",
      planTomorrow: "Odpri načrt za jutri",
      sectionTasks: "Naloge",
      sectionJournal: "Dnevnik",
      sectionQuick: "Hitro",
      unknown: "Neznana komanda",
    },
    undo: {
      taskAdded: "Dodano '{content}' za danes.",
      label: "Razveljavi",
    },
    ui: {
      contextAttached: "KONTEKST PRIPET",
      noContextYet: "ŠE BREZ KONTEKSTA",
      quickReply: "Hiter odgovor",
      quickReplies: "Hitri odgovori",
      orWriteYourOwn: "ali napiši po svoje",
      send: "pošlji",
      newline: "nova vrstica",
      commands: "ukazi",
      stop: "Ustavi",
      thinking: "Journail razmišlja…",
      navigate: "navigiraj",
      select: "izberi",
      complete: "dopolni",
      cancel: "prekliči",
    },
  },
  onboarding: {
    ariaLabel: "Onboarding vodič",
    eyebrow: "ZAČETEK",
    heading: { plain: "Šest", em: "majhnih", suffix: " korakov." },
    subtitle: "Ko jih dokončaš, bo dnevnik zaživel.",
    dismiss: "Skrij",
    stepLabel: "KORAK {n} OD {total}",
    showAll: "Pokaži vseh {total} korakov",
    hideAll: "Strni",
    currentBadge: "TRENUTNO",
    markDone: "Označi kot opravljeno",
    markNotDone: "Označi kot neopravljeno",
    allDoneHeading: { em: "Pripravljen", suffix: " si." },
    steps: {
      setLanguages: {
        title: "Izberi jezike",
        cta: "Nastavitve",
        hint: "V Nastavitvah → Jezik izberi jezik vmesnika (slovenščina ali angleščina) in jezik vsebine, v katerem se boš pogovarjal z AI in pisal dnevnik. Na voljo je preko 80 jezikov.",
      },
      firstGoal: {
        title: "Definiraj prvi cilj",
        cta: "Odpri cilje",
        hint: "Pojdi na Cilje in dodaj svojega prvega. Svoj cilj lahko kadarkoli preprosto zapišeš tudi v spodnjega AI pomočnika v naravnem jeziku (npr. »dodaj cilj: 5 km teka do poletja«).",
      },
      calendar: {
        title: "Poveži svoj koledar",
        cta: "Nastavitve",
        hint: "V Nastavitvah → Integracije se lahko povežeš s svojim Google koledarjem. Journail bo tvoje dogodke samodejno vključil kot kontekst pri načrtovanju dneva.",
      },
      taskSource: {
        title: "Izberi vir nalog",
        cta: "Odpri naloge",
        hint: "Ustvari svojo prvo nalogo na strani Naloge. Če ti je ljubše, lahko v Nastavitvah povežeš svoj Todoist in ga uporabljaš namesto internega sistema. Ne pozabi, da lahko naloge preprosto zapisuješ tudi prek AI pomočnika — le reci mu »dodaj nalogo …«.",
      },
      sideChat: {
        title: "Uporabi pomočnika",
        cta: "Vprašaj",
        hint: "Spodaj v AI pomočniku postavi katerokoli vprašanje — npr. »kaj imam še danes v načrtu« ali »v katerih dnevnikih sem pisal o Journailu«.",
      },
      firstPlanning: {
        title: "Zaženi prvi plan",
        cta: "Zaženi",
        hint: "V dnevniku zaženi današnji plan. AI te povede skozi prioritete, dogodke in cilje da skupaj pripravita plan današnjega dneva.",
      },
      firstEntry: {
        title: "Zaključi prvi dan",
        cta: "Pojdi",
        hint: "Zvečer ali po zaključku delavnika odpri dnevnik, skupaj z asistentom zapiši opravljeno delo in pritisni »Zaključi dan«. S tem boš naredil svojo prvo stran v dnevniku!",
      },
    },
  },
  trialGate: {
    title: "Tvoj brezplačni preizkus je potekel",
    subtitle: "Za nadaljevanje uporabe nadgradi na Pro ali Premium. Tvoji dnevniki, cilji in naloge ostanejo varno shranjeni.",
    signOut: "Odjava",
    publicSite: "Nazaj na javno stran",
  },
};
